// ---------------------------------------------------------------------------
// Canonical Shared Block Registry
// ---------------------------------------------------------------------------
// Owns the shared page-builder block contract used by:
//   1. AI component catalog exports
//   2. Editor block binding in app shells
//   3. SSR prop normalization before renderer execution

import type {
  BlockCapability,
  BlockOutputContract,
  BlockOutputRestrictionCode,
  BlockOutputSupport,
  CanonicalOutputKey,
} from "./output-contract/index.js";
import {
  buildBlockActionHref,
  normalizeBlockAction,
} from "./action-contract/index.js";
import type { BlockActionKind } from "./action-contract/index.js";

type BlockProps = Record<string, unknown>;
const ALL_PRIMARY_ACTION_KINDS = [
  "navigate",
  "submit_form",
  "book_service",
  "open_chat",
  "register_webinar",
  "claim_offer",
] as const satisfies readonly BlockActionKind[];
const BOOKING_ACTION_KINDS = [
  "navigate",
  "book_service",
] as const satisfies readonly BlockActionKind[];
const CHAT_ACTION_KINDS = [
  "navigate",
  "open_chat",
] as const satisfies readonly BlockActionKind[];
const FORM_ACTION_KINDS = [
  "submit_form",
] as const satisfies readonly BlockActionKind[];

export interface BlockActionContract {
  propName: "action";
  supportedKinds: readonly BlockActionKind[];
  legacyUrlProp?: string;
}

export interface PropDef {
  name: string;
  type: string;
  required: boolean;
  description: string;
  defaultValue?: string;
}

export interface BlockDefinition {
  type: string;
  label: string;
  description: string;
  usageGuidance: string;
  props: readonly PropDef[];
  outputs: BlockOutputContract;
  actionContract?: BlockActionContract;
  editorKey?: string;
  hasZones?: boolean;
  zoneNames?: readonly string[];
  normalizeForRender?: (props: BlockProps) => BlockProps;
}

type BlockRegistryEntry = Omit<BlockDefinition, "outputs">;

function outputSupport(
  outputKey: CanonicalOutputKey,
  level: BlockOutputSupport["level"],
  restrictionCode?: BlockOutputRestrictionCode,
  note?: string,
): BlockOutputSupport {
  return { outputKey, level, restrictionCode, note };
}

function blockOutputs(
  capabilities: readonly BlockCapability[],
  support: readonly BlockOutputSupport[],
): BlockOutputContract {
  return { capabilities, support };
}

const DEFAULT_WEB_BLOCK_OUTPUTS = blockOutputs(
  ["content"],
  [
    outputSupport("page", "full"),
    outputSupport("funnel", "full"),
    outputSupport("email", "unsupported", "output_not_supported", "Email output has a separate rendering contract."),
    outputSupport("lesson", "full"),
    outputSupport("certificate", "limited", "certificate_read_only", "Certificates allow only low-interaction presentation blocks."),
    outputSupport("display", "unsupported", "output_not_supported", "Display output is planned but not yet wired to the renderer."),
  ],
);

const BLOCK_OUTPUTS_BY_TYPE = {
  "hero-section": blockOutputs(
    ["content", "conversion"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Email output has a separate rendering contract."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "limited", "certificate_read_only", "Certificates allow only low-interaction presentation blocks."),
      outputSupport("display", "full"),
    ],
  ),
  "lead-form": blockOutputs(
    ["conversion", "form"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "web_runtime_only", "Form submission handling is currently web-only."),
      outputSupport("lesson", "limited", "course_context_only", "Use only when a lesson explicitly needs a submission form."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates must remain non-interactive."),
      outputSupport("display", "limited", "interactive_runtime_only", "Displays support forms only for curated kiosk interactions."),
    ],
  ),
  "multi-step-form": blockOutputs(
    ["conversion", "form"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "web_runtime_only", "Multi-step progression requires browser runtime state."),
      outputSupport("lesson", "limited", "course_context_only", "Use only in assessment-oriented lessons."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates must remain non-interactive."),
      outputSupport("display", "limited", "interactive_runtime_only", "Displays support only curated kiosk-style multi-step flows."),
    ],
  ),
  "text-block": blockOutputs(
    ["content"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Email output has a separate rendering contract."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "limited", "certificate_read_only", "Certificates allow only low-interaction presentation blocks."),
      outputSupport("display", "full"),
    ],
  ),
  "cta-banner": blockOutputs(
    ["content", "conversion"],
    DEFAULT_WEB_BLOCK_OUTPUTS.support,
  ),
  "testimonial-grid": blockOutputs(
    ["content", "proof"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Email output has a separate rendering contract."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "limited", "certificate_read_only", "Certificates allow only low-interaction presentation blocks."),
      outputSupport("display", "full"),
    ],
  ),
  "reviews-grid": blockOutputs(
    ["content", "proof"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Email output has a separate rendering contract."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "limited", "certificate_read_only", "Certificates allow only low-interaction presentation blocks."),
      outputSupport("display", "full"),
    ],
  ),
  "pricing-table": blockOutputs(
    ["commerce", "conversion"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Pricing-table email markup is not yet part of the email renderer."),
      outputSupport("lesson", "limited", "course_context_only", "Use only for upgrade or offer lessons."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should not render price comparisons."),
      outputSupport("display", "unsupported", "output_not_supported", "Display output is not wired for pricing interactions."),
    ],
  ),
  "video-embed": blockOutputs(
    ["media", "runtime"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "interactive_runtime_only", "Embedded video playback is not available in email mode."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should render static proof only."),
      outputSupport("display", "limited", "publish_runtime_only", "Display mode supports video only where device autoplay constraints are satisfied."),
    ],
  ),
  "countdown-timer": blockOutputs(
    ["conversion", "runtime"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "interactive_runtime_only", "Live countdown behavior is not supported in email mode."),
      outputSupport("lesson", "limited", "course_context_only", "Only use for time-bound lesson flows."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should not have expiring UI."),
      outputSupport("display", "limited", "publish_runtime_only", "Display mode timing rules are not finalized."),
    ],
  ),
  "calendar-embed": blockOutputs(
    ["conversion", "embed", "runtime"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "interactive_runtime_only", "Calendar embeds require browser runtime."),
      outputSupport("lesson", "limited", "course_context_only", "Only use when a lesson needs booking."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should not embed booking widgets."),
      outputSupport("display", "unsupported", "interactive_runtime_only", "Displays do not support booking interaction."),
    ],
  ),
  "faq-accordion": DEFAULT_WEB_BLOCK_OUTPUTS,
  "image-gallery": blockOutputs(
    ["content", "media"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Email output has a separate rendering contract."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "limited", "certificate_read_only", "Certificates allow only low-interaction presentation blocks."),
      outputSupport("display", "full"),
    ],
  ),
  "social-proof": blockOutputs(
    ["content", "proof"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Email output has a separate rendering contract."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "limited", "certificate_read_only", "Certificates allow only low-interaction presentation blocks."),
      outputSupport("display", "full"),
    ],
  ),
  "navigation-bar": blockOutputs(
    ["navigation"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "limited", "navigation_discouraged", "Funnel navigation should be minimized to avoid exits."),
      outputSupport("email", "unsupported", "output_not_supported", "Email output has no site-level navigation."),
      outputSupport("lesson", "limited", "course_context_only", "Lessons may use nav sparingly within course shells."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should avoid navigational chrome."),
      outputSupport("display", "unsupported", "output_not_supported", "Display output does not use site navigation."),
    ],
  ),
  "popup-trigger": blockOutputs(
    ["conversion", "runtime"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "interactive_runtime_only", "Popups require browser event triggers."),
      outputSupport("lesson", "limited", "course_context_only", "Use sparingly in learning flows."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates must remain static."),
      outputSupport("display", "unsupported", "interactive_runtime_only", "Displays do not support popup behavior."),
    ],
  ),
  "tracking-pixel": blockOutputs(
    ["tracking", "runtime"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Email tracking uses a separate delivery contract."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "limited", "certificate_read_only", "Certificates may emit verification analytics only."),
      outputSupport("display", "limited", "publish_runtime_only", "Display analytics are planned but not fully wired."),
    ],
  ),
  "blog-post-card": blockOutputs(
    ["content"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "limited", "navigation_discouraged", "Use only when a funnel intentionally cross-links content."),
      outputSupport("email", "unsupported", "output_not_supported", "Email post cards need a dedicated renderer contract."),
      outputSupport("lesson", "limited", "course_context_only", "Use only for recommended reading."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should not render blog promotion."),
      outputSupport("display", "unsupported", "output_not_supported", "Display output is not wired for blog cards."),
    ],
  ),
  "blog-listing-grid": blockOutputs(
    ["content", "navigation"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "limited", "navigation_discouraged", "Funnel use should be deliberate and rare."),
      outputSupport("email", "unsupported", "output_not_supported", "Email listing grids need a separate renderer."),
      outputSupport("lesson", "limited", "course_context_only", "Use only when supporting lesson references."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should not include discovery grids."),
      outputSupport("display", "unsupported", "output_not_supported", "Display output is not wired for listing grids."),
    ],
  ),
  "order-bump": blockOutputs(
    ["commerce", "conversion"],
    [
      outputSupport("page", "unsupported", "funnel_context_only", "Order bumps are only valid within funnel commerce flows."),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Email output cannot host order-bump interaction."),
      outputSupport("lesson", "unsupported", "funnel_context_only", "Lessons should not render checkout-only upsells."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should not include upsells."),
      outputSupport("display", "unsupported", "output_not_supported", "Display output does not support order-bump flows."),
    ],
  ),
  "booking-widget": blockOutputs(
    ["conversion", "embed", "runtime"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "interactive_runtime_only", "Booking selection requires browser runtime."),
      outputSupport("lesson", "limited", "course_context_only", "Use only where booking is part of course delivery."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should not include booking interaction."),
      outputSupport("display", "limited", "interactive_runtime_only", "Displays support booking widgets only for curated kiosk flows."),
    ],
  ),
  "feature-grid": blockOutputs(
    ["content", "conversion"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Email output has a separate rendering contract."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "limited", "certificate_read_only", "Certificates allow only low-interaction presentation blocks."),
      outputSupport("display", "full"),
    ],
  ),
  "logo-cloud": blockOutputs(
    ["content", "proof"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Email output has a separate rendering contract."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "limited", "certificate_read_only", "Certificates allow only low-interaction presentation blocks."),
      outputSupport("display", "full"),
    ],
  ),
  "stats-counter": blockOutputs(
    ["content", "proof"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Email output has a separate rendering contract."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "limited", "certificate_read_only", "Certificates allow only low-interaction presentation blocks."),
      outputSupport("display", "full"),
    ],
  ),
  "comparison-table": blockOutputs(
    ["content", "commerce"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "output_not_supported", "Email comparison tables need dedicated markup."),
      outputSupport("lesson", "limited", "course_context_only", "Use only for instructional comparisons."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should not present comparison UI."),
      outputSupport("display", "full"),
    ],
  ),
  "roi-calculator": blockOutputs(
    ["conversion", "runtime"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "interactive_runtime_only", "ROI slider interaction requires browser runtime."),
      outputSupport("lesson", "limited", "course_context_only", "Use only when the lesson includes a decision-support calculator."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should not include interactive calculators."),
      outputSupport("display", "limited", "interactive_runtime_only", "Display output supports calculators only for curated kiosk flows."),
    ],
  ),
  "social-proof-popup": blockOutputs(
    ["proof", "runtime", "conversion"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "interactive_runtime_only", "Notification popups require browser runtime."),
      outputSupport("lesson", "limited", "course_context_only", "Use only where lesson-side proof messages are intentional."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should remain static."),
      outputSupport("display", "unsupported", "interactive_runtime_only", "Display mode does not use visitor-toast runtime."),
    ],
  ),
  "before-after": blockOutputs(
    ["content", "media", "runtime"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "interactive_runtime_only", "Before/after sliders require browser runtime."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "limited", "certificate_read_only", "Certificates can show the comparison but should avoid interaction-heavy copy."),
      outputSupport("display", "full"),
    ],
  ),
  "embed-widget": blockOutputs(
    ["embed", "runtime"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "full"),
      outputSupport("email", "unsupported", "interactive_runtime_only", "Iframes are not supported in email mode."),
      outputSupport("lesson", "full"),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should avoid interactive embeds."),
      outputSupport("display", "limited", "publish_runtime_only", "Display-mode embeds require curated device-safe hosts."),
    ],
  ),
  "chat-widget": blockOutputs(
    ["conversion", "runtime"],
    [
      outputSupport("page", "full"),
      outputSupport("funnel", "limited", "navigation_discouraged", "Use only where funnel messaging is intentional."),
      outputSupport("email", "unsupported", "interactive_runtime_only", "Live chat requires browser runtime."),
      outputSupport("lesson", "limited", "course_context_only", "Use only for coached lesson experiences."),
      outputSupport("certificate", "unsupported", "certificate_read_only", "Certificates should remain static."),
      outputSupport("display", "unsupported", "interactive_runtime_only", "Displays do not support live chat."),
    ],
  ),
} as const satisfies Record<string, BlockOutputContract>;

function asString(value: unknown): string {
  return typeof value === "string" ? value : typeof value === "number" ? String(value) : "";
}

function toInt(value: unknown, fallback: number, min: number, max: number): number {
  const parsed = typeof value === "number" ? value : Number.parseInt(asString(value), 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(min, Math.min(max, Math.floor(parsed)));
}

function yesNoToBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value;
  if (value === "yes") return true;
  if (value === "no") return false;
  return fallback;
}

function splitLines(value: unknown): string[] {
  return asString(value)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function splitPair(line: string): [string, string] {
  const [left, right] = line.split("|").map((part) => part.trim());
  return [left ?? "", right ?? ""];
}

function splitMany(line: string): string[] {
  return line.split("|").map((part) => part.trim());
}

function normalizeBlockActionProps(
  props: BlockProps,
  actionContract?: BlockActionContract,
): BlockProps {
  if (!actionContract) {
    return props;
  }

  const normalizedAction = normalizeBlockAction(props[actionContract.propName]);
  const legacyUrl = actionContract.legacyUrlProp
    ? asString(props[actionContract.legacyUrlProp])
    : "";
  const supportedAction = normalizedAction && actionContract.supportedKinds.includes(normalizedAction.kind)
    ? normalizedAction
    : null;
  const action = supportedAction ?? (legacyUrl
    ? { kind: "navigate" as const, url: legacyUrl }
    : null);

  if (!action) {
    return props;
  }

  const nextProps: BlockProps = {
    ...props,
    [actionContract.propName]: action,
  };

  if (actionContract.legacyUrlProp && !legacyUrl) {
    const href = buildBlockActionHref(action);
    if (href) {
      nextProps[actionContract.legacyUrlProp] = href;
    }
  }

  return nextProps;
}

function slugifyFieldName(label: string, fallback: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return slug || fallback;
}

function placeholderSvgDataUri(label: string): string {
  const text = label.trim() || "Logo";
  const safeText = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="96" viewBox="0 0 240 96" role="img" aria-label="${safeText}">
  <rect width="240" height="96" rx="12" fill="#f3f4f6"/>
  <text x="120" y="52" text-anchor="middle" font-family="Arial, sans-serif" font-size="18" fill="#6b7280">${safeText}</text>
</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function parseLeadFormFields(fieldList: unknown): Array<Record<string, unknown>> {
  return splitLines(fieldList).map((line, index) => {
    const [label, type] = splitPair(line);
    const normalizedType = type || "text";
    return {
      name: slugifyFieldName(label, `field_${index + 1}`),
      label,
      type: normalizedType,
      placeholder: label,
      required: true,
    };
  });
}

function normalizeReviewsGridProps(props: BlockProps): BlockProps {
  // The reviews-grid renderer accepts an already-hydrated `reviews` array
  // produced by the published-reviews loader (joins `reviews` ⨝
  // `reviewWidgetReviewDecisions` filtered to status='approved' for the
  // tenant). The normalizer's job is to keep prop shape stable across
  // hydration, AI generation, and template seeds — never to fetch data.
  const reviews = Array.isArray(props.reviews)
    ? props.reviews
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const record = item as Record<string, unknown>;
          const id = asString(record.id);
          if (!id) return null;
          return {
            id,
            authorName: asString(record.authorName || record.author),
            text: asString(record.text || record.quote),
            rating: toInt(record.rating, 5, 0, 5),
            publishedAt: asString(record.publishedAt),
            sourceType: asString(record.sourceType),
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null)
    : [];

  return { ...props, reviews };
}

function normalizeTestimonialsProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.testimonials)) return props;
  if (Array.isArray(props.quotes)) {
    const testimonials = props.quotes
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const record = item as Record<string, unknown>;
        return {
          quote: asString(record.quote),
          name: asString(record.author || record.name),
          role: asString(record.role),
          company: asString(record.company),
          avatarUrl: asString(record.avatarUrl || record.imageUrl),
          rating: toInt(record.rating, 5, 0, 5),
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .filter((item) => item.quote || item.name || item.role || item.company || item.avatarUrl);

    return { ...props, testimonials };
  }
  const testimonials = splitLines(props.quotes).map((line) => {
    const [quote, name, role] = splitMany(line);
    return {
      quote,
      name,
      role,
      company: "",
      avatarUrl: "",
      rating: 5,
    };
  });

  return { ...props, testimonials };
}

function normalizePricingTableProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.tiers)) return props;
  const tiers = [1, 2, 3]
    .map((index) => {
      const name = asString(props[`tier${index}Name`]);
      const price = asString(props[`tier${index}Price`]);
      const features = splitLines(props[`tier${index}Features`]);
      if (!name && !price && features.length === 0) return null;
      return {
        name,
        price,
        period: "",
        features,
        ctaText: `Choose ${name || `Tier ${index}`}`,
        ctaUrl: "#form",
        isPopular: index === 2,
      };
    })
    .filter((tier): tier is NonNullable<typeof tier> => tier !== null);

  return { ...props, tiers };
}

function normalizeVideoEmbedProps(props: BlockProps): BlockProps {
  if (typeof props.videoUrl === "string" && !props.url) return props;
  return { ...props, videoUrl: asString(props.url || props.videoUrl) };
}

function normalizeFaqProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.items)) return props;
  const items = splitLines(props.items).map((line) => {
    const [question, answer] = splitPair(line);
    return { question, answer };
  });

  return { ...props, items, openFirst: yesNoToBool(props.openFirst, true) };
}

function normalizeGalleryProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.images)) {
    return {
      ...props,
      columns: toInt(props.columns, 3, 2, 4),
    };
  }
  const images = splitLines(props.images).map((src, index) => ({
    src,
    alt: `Gallery image ${index + 1}`,
    caption: "",
  }));

  return {
    ...props,
    images,
    columns: toInt(props.columns, 3, 2, 4),
  };
}

function normalizeFeatureGridProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.features)) {
    return {
      ...props,
      columns: toInt(props.columns, 3, 2, 4),
    };
  }
  const features = splitLines(props.features).map((line) => {
    const [heading, description] = splitPair(line);
    return {
      icon: "✦",
      heading,
      description,
    };
  });

  return {
    ...props,
    features,
    columns: toInt(props.columns, 3, 2, 4),
  };
}

function normalizeStatsCounterProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.items)) return props;
  if (Array.isArray(props.stats)) {
    const items = props.stats
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const record = item as Record<string, unknown>;
        return {
          value: asString(record.number || record.value),
          label: asString(record.label),
          prefix: asString(record.prefix),
          suffix: asString(record.suffix),
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .filter((item) => item.value || item.label);

    return { ...props, items };
  }
  const items = splitLines(props.stats).map((line) => {
    const [value, label] = splitPair(line);
    return { value, label };
  });

  return { ...props, items };
}

function normalizeLeadFormProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.fields)) {
    const fieldList = props.fields
      .map((field) => {
        if (!field || typeof field !== "object") return null;
        const label = asString((field as Record<string, unknown>).label);
        const type = asString((field as Record<string, unknown>).type) || "text";
        return label ? `${label} | ${type}` : null;
      })
      .filter((line): line is string => Boolean(line))
      .join("\n");

    return normalizeBlockActionProps(
      { ...props, fieldList },
      {
        propName: "action",
        supportedKinds: FORM_ACTION_KINDS,
      },
    );
  }

  return normalizeBlockActionProps(
    { ...props, fields: parseLeadFormFields(props.fieldList) },
    {
      propName: "action",
      supportedKinds: FORM_ACTION_KINDS,
    },
  );
}

function normalizeMultiStepFormProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.steps)) {
    return normalizeBlockActionProps(
      {
        ...props,
        showProgress: typeof props.showProgress === "boolean"
          ? props.showProgress
          : yesNoToBool(props.showProgress, true),
      },
      {
        propName: "action",
        supportedKinds: FORM_ACTION_KINDS,
      },
    );
  }
  const requestedCount = toInt(props.stepCount, 3, 1, 10);
  const titles = splitLines(props.stepTitles);
  const steps = Array.from({ length: requestedCount }, (_, index) => ({
    title: titles[index] ?? `Step ${index + 1}`,
    fields: [
      {
        name: `step_${index + 1}_field_1`,
        label: "Field 1",
        type: "text",
        placeholder: "Field 1",
        required: true,
      },
      {
        name: `step_${index + 1}_field_2`,
        label: "Field 2",
        type: "text",
        placeholder: "Field 2",
        required: true,
      },
    ],
  }));

  return normalizeBlockActionProps(
    {
      ...props,
      steps,
      showProgress: yesNoToBool(props.showProgress, true),
    },
    {
      propName: "action",
      supportedKinds: FORM_ACTION_KINDS,
    },
  );
}

function normalizeHeroSectionProps(props: BlockProps): BlockProps {
  return normalizeBlockActionProps(props, {
    propName: "action",
    supportedKinds: ALL_PRIMARY_ACTION_KINDS,
    legacyUrlProp: "ctaUrl",
  });
}

function normalizeCtaBannerProps(props: BlockProps): BlockProps {
  return normalizeBlockActionProps(props, {
    propName: "action",
    supportedKinds: ALL_PRIMARY_ACTION_KINDS,
    legacyUrlProp: "ctaUrl",
  });
}

function normalizePopupTriggerProps(props: BlockProps): BlockProps {
  return normalizeBlockActionProps(props, {
    propName: "action",
    supportedKinds: ALL_PRIMARY_ACTION_KINDS,
    legacyUrlProp: "ctaUrl",
  });
}

function normalizeServiceBookingProps(props: BlockProps): BlockProps {
  const serviceId = asString(props.serviceId || props.calendarId);
  return normalizeBlockActionProps(
    {
      ...props,
      serviceId,
      duration: toInt(props.duration, 30, 5, 240),
      showTimezone: yesNoToBool(props.showTimezone, true),
    },
    {
      propName: "action",
      supportedKinds: BOOKING_ACTION_KINDS,
      legacyUrlProp: "bookingUrl",
    },
  );
}

function normalizeLogoCloudProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.logos)) {
    return {
      ...props,
      grayscale: typeof props.grayscale === "boolean"
        ? props.grayscale
        : yesNoToBool(props.grayscale, true),
    };
  }
  const count = toInt(props.logoCount, 5, 1, 12);
  const logos = Array.from({ length: count }, (_, index) => {
    const label = `Logo ${index + 1}`;
    return {
      imageUrl: placeholderSvgDataUri(label),
      alt: label,
    };
  });

  return {
    ...props,
    logos,
    grayscale: yesNoToBool(props.grayscale, true),
  };
}

function normalizeNavigationBarProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.links)) {
    return {
      ...props,
      sticky: typeof props.sticky === "boolean"
        ? props.sticky
        : yesNoToBool(props.sticky, true),
    };
  }
  const links = splitLines(props.linkList).map((line) => {
    const [label, url] = splitPair(line);
    return { label, url: url || "#" };
  });

  return {
    ...props,
    links,
    sticky: yesNoToBool(props.sticky, true),
  };
}

function normalizeSocialProofProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.items)) return props;
  const variant = asString(props.variant) || "stats";
  const items = splitLines(props.items).map((line) => {
    const [value, label] = splitPair(line);
    if (variant === "stats") {
      return { value, label };
    }
    const alt = label || value || "Badge";
    return {
      imageUrl: placeholderSvgDataUri(alt),
      alt,
    };
  });

  return { ...props, items };
}

function normalizeComparisonTableProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.columns) && Array.isArray(props.rows)) {
    return normalizeBlockActionProps(
      props,
      {
        propName: "action",
        supportedKinds: ALL_PRIMARY_ACTION_KINDS,
        legacyUrlProp: "ctaUrl",
      },
    );
  }
  const columns = splitLines(props.columns).map((line) => ({
    label: line.replace(/^\*/, ""),
    isHighlighted: line.startsWith("*"),
  }));

  const rows = splitLines(props.rows).map((line) => {
    const [feature, ...values] = splitMany(line);
    return {
      feature,
      values: values.map((value) => {
        if (value === "yes") return true;
        if (value === "no") return false;
        return value;
      }),
    };
  });

  return normalizeBlockActionProps(
    { ...props, columns, rows },
    {
      propName: "action",
      supportedKinds: ALL_PRIMARY_ACTION_KINDS,
      legacyUrlProp: "ctaUrl",
    },
  );
}

function normalizeRoiCalculatorProps(props: BlockProps): BlockProps {
  return normalizeBlockActionProps(
    {
      ...props,
      primaryMin: toInt(props.primaryMin, 10, 0, 1_000_000),
      primaryMax: toInt(props.primaryMax, 500, 1, 1_000_000),
      primaryDefault: toInt(props.primaryDefault, 120, 0, 1_000_000),
      primaryStep: toInt(props.primaryStep, 10, 1, 100_000),
      secondaryMin: toInt(props.secondaryMin, 100, 0, 1_000_000),
      secondaryMax: toInt(props.secondaryMax, 5000, 1, 1_000_000),
      secondaryDefault: toInt(props.secondaryDefault, 850, 0, 1_000_000),
      secondaryStep: toInt(props.secondaryStep, 50, 1, 100_000),
      multiplier: toInt(props.multiplier, 12, 1, 365),
      formula: ["multiply", "percentage", "compound"].includes(asString(props.formula))
        ? asString(props.formula)
        : "multiply",
    },
    {
      propName: "action",
      supportedKinds: ALL_PRIMARY_ACTION_KINDS,
      legacyUrlProp: "ctaUrl",
    },
  );
}

function normalizeSocialProofPopupProps(props: BlockProps): BlockProps {
  const notifications = Array.isArray(props.notifications)
    ? props.notifications
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const record = item as Record<string, unknown>;
        return {
          name: asString(record.name),
          location: asString(record.location),
          action: asString(record.action),
          timeAgo: asString(record.timeAgo),
        };
      })
      .filter((item): item is { name: string; location: string; action: string; timeAgo: string } => item !== null)
      .filter((item) => item.name || item.location || item.action || item.timeAgo)
    : splitLines(props.notifications).map((line) => {
      const [name, location, action, timeAgo] = splitMany(line);
      return {
        name,
        location,
        action,
        timeAgo,
      };
    });

  const position = ["bottom-left", "bottom-right", "top-left", "top-right"].includes(asString(props.position))
    ? asString(props.position)
    : "bottom-left";

  const boundedNotifications = notifications.slice(0, 20);
  const boundedMaxShown = Math.min(
    boundedNotifications.length || 1,
    toInt(props.maxShown, 5, 1, 20),
  );

  return {
    ...props,
    notifications: boundedNotifications,
    displayInterval: toInt(props.displayInterval, 10, 3, 120),
    displayDuration: toInt(props.displayDuration, 4, 2, 30),
    position,
    showOnMobile: yesNoToBool(props.showOnMobile, true),
    maxShown: boundedMaxShown,
    randomize: yesNoToBool(props.randomize, false),
  };
}

function normalizeEmbedWidgetProps(props: BlockProps): BlockProps {
  return {
    ...props,
    allowFullscreen: yesNoToBool(props.allowFullscreen, false),
  };
}

function normalizeChatWidgetProps(props: BlockProps): BlockProps {
  return normalizeBlockActionProps(props, {
    propName: "action",
    supportedKinds: CHAT_ACTION_KINDS,
  });
}

function splitCommaSeparated(value: unknown): string[] {
  return asString(value)
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function normalizeVideoPlayerProps(props: BlockProps): BlockProps {
  return {
    ...props,
    showProgress: yesNoToBool(props.showProgress, true),
    autoPlay: yesNoToBool(props.autoPlay, false),
    completionThreshold: toInt(props.completionThreshold, 90, 0, 100),
  };
}

function normalizeProgressBarProps(props: BlockProps): BlockProps {
  return {
    ...props,
    showPercent: yesNoToBool(props.showPercent, true),
    currentPercent: toInt(props.currentPercent, 0, 0, 100),
  };
}

function normalizeExerciseBlockProps(props: BlockProps): BlockProps {
  return {
    ...props,
    acceptedFileTypes: Array.isArray(props.acceptedFileTypes)
      ? props.acceptedFileTypes
      : splitCommaSeparated(props.acceptedFileTypes),
    maxFileSizeMb: toInt(props.maxFileSizeMb, 50, 1, 10_000),
    required: yesNoToBool(props.required, true),
  };
}

function resolveCorrectOptionId(
  options: string[],
  rawCorrectAnswer: string,
): string {
  const normalized = rawCorrectAnswer.trim().toLowerCase();
  const index = Number.parseInt(normalized, 10);
  if (!Number.isNaN(index) && index >= 1 && index <= options.length) {
    return `option-${index}`;
  }

  const foundIndex = options.findIndex(
    (option) => option.trim().toLowerCase() === normalized,
  );
  return foundIndex >= 0 ? `option-${foundIndex + 1}` : "option-1";
}

function normalizeQuizBlockProps(props: BlockProps): BlockProps {
  if (Array.isArray(props.questions)) {
    return {
      ...props,
      passingScore: toInt(props.passingScore, 70, 0, 100),
      maxAttempts: toInt(props.maxAttempts, 3, 1, 100),
      shuffleQuestions: yesNoToBool(props.shuffleQuestions, false),
      shuffleOptions: yesNoToBool(props.shuffleOptions, false),
      showExplanations: yesNoToBool(props.showExplanations, true),
      timeLimitMinutes: props.timeLimitMinutes === undefined
        ? undefined
        : toInt(props.timeLimitMinutes, 0, 0, 10_000),
    };
  }

  const questions = splitLines(props.questionList).map((line, index) => {
    const [text, ...rawParts] = splitMany(line);
    const correctAnswer = rawParts[rawParts.length - 1] ?? "";
    const optionLabels = rawParts.slice(0, Math.max(rawParts.length - 1, 0));
    const options = optionLabels.length > 0 ? optionLabels : ["True", "False"];
    const normalizedOptions = options.map((option, optionIndex) => ({
      id: `option-${optionIndex + 1}`,
      text: option,
    }));
    const isTrueFalse = normalizedOptions.length === 2
      && normalizedOptions.every((option) =>
        ["true", "false"].includes(option.text.trim().toLowerCase()),
      );

    return {
      id: `question-${index + 1}`,
      type: isTrueFalse ? "true_false" : "multiple_choice",
      text,
      options: normalizedOptions,
      correctOptionId: resolveCorrectOptionId(options, correctAnswer),
      points: 1,
    };
  });

  return {
    ...props,
    questions,
    passingScore: toInt(props.passingScore, 70, 0, 100),
    maxAttempts: toInt(props.maxAttempts, 3, 1, 100),
    shuffleQuestions: yesNoToBool(props.shuffleQuestions, false),
    shuffleOptions: yesNoToBool(props.shuffleOptions, false),
    showExplanations: yesNoToBool(props.showExplanations, true),
    timeLimitMinutes: props.timeLimitMinutes === undefined
      ? undefined
      : toInt(props.timeLimitMinutes, 0, 0, 10_000),
  };
}

function normalizeDripGateProps(props: BlockProps): BlockProps {
  return {
    ...props,
    delayDays: props.delayDays === undefined
      ? undefined
      : toInt(props.delayDays, 0, 0, 10_000),
    requiredScore: props.requiredScore === undefined
      ? undefined
      : toInt(props.requiredScore, 70, 0, 100),
  };
}

const SHARED_BLOCK_REGISTRY_DATA: readonly BlockRegistryEntry[] = [
  {
    type: "hero-section",
    editorKey: "hero-section",
    label: "Hero Section",
    description: "Full-width hero banner with headline, subheadline, CTA button, and optional background image.",
    usageGuidance: "Always place as the first component. Use for the main value proposition. Include a clear CTA.",
    props: [
      { name: "headline", type: "string", required: true, description: "Main headline (H1). Keep under 10 words for impact." },
      { name: "subheadline", type: "string", required: false, description: "Supporting text below headline. 1-2 sentences max." },
      { name: "ctaText", type: "string", required: true, description: "CTA button label.", defaultValue: "Get Started" },
      { name: "action", type: "BlockAction", required: false, description: "Canonical typed CTA action." },
      { name: "ctaUrl", type: "string", required: false, description: "CTA destination URL.", defaultValue: "#form" },
      { name: "backgroundImage", type: "string", required: false, description: "Background image URL." },
      { name: "backgroundOverlay", type: "number", required: false, description: "Overlay opacity 0-100.", defaultValue: "40" },
      { name: "alignment", type: "'left' | 'center' | 'right'", required: false, description: "Text alignment.", defaultValue: "center" },
      { name: "minHeight", type: "string", required: false, description: "Minimum section height.", defaultValue: "80vh" },
    ],
    actionContract: {
      propName: "action",
      supportedKinds: ALL_PRIMARY_ACTION_KINDS,
      legacyUrlProp: "ctaUrl",
    },
    normalizeForRender: normalizeHeroSectionProps,
  },
  {
    type: "lead-form",
    editorKey: "LeadForm",
    label: "Lead Capture Form",
    description: "Single-step lead capture form preview with simplified field-list editing in the builder.",
    usageGuidance: "Use for lead capture, newsletter signup, or contact forms. Keep to 3-5 fields for best conversion.",
    props: [
      { name: "heading", type: "string", required: false, description: "Form heading text." },
      { name: "submitText", type: "string", required: true, description: "Submit button label.", defaultValue: "Submit" },
      { name: "action", type: "BlockAction", required: false, description: "Canonical typed form submit action." },
      { name: "successMessage", type: "string", required: false, description: "Message shown after successful submission.", defaultValue: "Thank you! We'll be in touch." },
      { name: "consentText", type: "string", required: false, description: "GDPR consent checkbox text." },
      { name: "crmFollowUpPolicy", type: "'follow_up' | 'contact_only' | 'create_deal'", required: false, description: "CRM automation policy for submitted leads.", defaultValue: "follow_up" },
      { name: "fieldList", type: "textarea", required: true, description: "One field per line in the format `Label | type`.", defaultValue: "Full Name | text\\nEmail | email\\nMessage | textarea" },
    ],
    actionContract: {
      propName: "action",
      supportedKinds: FORM_ACTION_KINDS,
    },
    normalizeForRender: normalizeLeadFormProps,
  },
  {
    type: "multi-step-form",
    editorKey: "MultiStepForm",
    label: "Multi-Step Form",
    description: "Multi-step form preview with generated placeholder fields for each step.",
    usageGuidance: "Use when you need more than a few fields. Keep the step count low and titles clear.",
    props: [
      { name: "heading", type: "string", required: false, description: "Form heading text." },
      { name: "stepCount", type: "number", required: true, description: "Number of steps to preview.", defaultValue: "3" },
      { name: "stepTitles", type: "textarea", required: true, description: "One step title per line." },
      { name: "submitText", type: "string", required: true, description: "Final submit button label.", defaultValue: "Complete" },
      { name: "action", type: "BlockAction", required: false, description: "Canonical typed form submit action." },
      { name: "showProgress", type: "'yes' | 'no'", required: false, description: "Show step progress bar.", defaultValue: "yes" },
    ],
    actionContract: {
      propName: "action",
      supportedKinds: FORM_ACTION_KINDS,
    },
    normalizeForRender: normalizeMultiStepFormProps,
  },
  {
    type: "text-block",
    editorKey: "TextBlock",
    label: "Text Block",
    description: "Rich text content block with heading and body.",
    usageGuidance: "Use for body copy, feature descriptions, and about sections.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading." },
      { name: "content", type: "textarea", required: true, description: "Body content." },
      { name: "alignment", type: "'left' | 'center' | 'right'", required: false, description: "Text alignment.", defaultValue: "left" },
      { name: "maxWidth", type: "string", required: false, description: "Maximum content width.", defaultValue: "720px" },
    ],
  },
  {
    type: "cta-banner",
    editorKey: "CtaBanner",
    label: "CTA Banner",
    description: "Full-width call-to-action strip with headline, subtext, and button.",
    usageGuidance: "Place between content sections to reinforce the conversion goal. Use the canonical action contract instead of ad hoc CTA wiring.",
    props: [
      { name: "headline", type: "string", required: true, description: "CTA headline." },
      { name: "subtext", type: "string", required: false, description: "Supporting text below the headline." },
      { name: "ctaText", type: "string", required: true, description: "Button label." },
      { name: "action", type: "BlockAction", required: false, description: "Canonical typed CTA action." },
      { name: "ctaUrl", type: "string", required: false, description: "Legacy button link fallback.", defaultValue: "#form" },
      { name: "variant", type: "'primary' | 'secondary' | 'accent'", required: false, description: "Color scheme variant.", defaultValue: "primary" },
    ],
    actionContract: {
      propName: "action",
      supportedKinds: ALL_PRIMARY_ACTION_KINDS,
      legacyUrlProp: "ctaUrl",
    },
    normalizeForRender: normalizeCtaBannerProps,
  },
  {
    type: "testimonial-grid",
    editorKey: "Testimonials",
    label: "Testimonial Grid",
    description: "Customer testimonial section edited as structured testimonial cards in the builder.",
    usageGuidance: "Place after the offer and before the final form or CTA.",
    props: [
      { name: "quotes", type: "TestimonialItem[]", required: true, description: "Structured testimonial items with `quote`, `author`, and `role`. Legacy pipe-delimited textarea values are still accepted on read." },
    ],
    normalizeForRender: normalizeTestimonialsProps,
  },
  {
    type: "reviews-grid",
    editorKey: "Reviews",
    label: "Reviews",
    description: "Live customer reviews pulled from the unified review database. Only reviews approved via the moderation widget surface here. Distinct from `testimonial-grid`, which holds manually edited quotes.",
    usageGuidance: "Use when you want the published page to track real reviews from Google or Facebook automatically. Place after the offer; the empty state keeps layout stable when no reviews are approved yet.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "What Customers Are Saying" },
      { name: "reviews", type: "PublishedReviewItem[]", required: true, description: "Hydrated by the published-reviews loader at render time. Each item has `id`, `authorName`, `text`, `rating`, `publishedAt`, and `sourceType`." },
      { name: "columns", type: "number", required: false, description: "Card grid columns (clamped 1..4).", defaultValue: "3" },
      { name: "showRating", type: "'yes' | 'no'", required: false, description: "Show the star rating row on each card.", defaultValue: "yes" },
      { name: "showSource", type: "'yes' | 'no'", required: false, description: "Show the source platform badge (Google, Facebook).", defaultValue: "yes" },
      { name: "emptyStateMessage", type: "string", required: false, description: "Copy shown when no reviews are approved yet." },
      { name: "tenantId", type: "string", required: false, description: "Tenant identifier — written to a data-attribute for analytics scoping. Set by the loader to match its query scope." },
    ],
    normalizeForRender: normalizeReviewsGridProps,
  },
  {
    type: "pricing-table",
    editorKey: "PricingTable",
    label: "Pricing Table",
    description: "Pricing comparison table edited as structured tier cards in the builder.",
    usageGuidance: "Use on sales pages. Highlight the recommended tier.",
    props: [
      { name: "tiers", type: "PricingTierItem[]", required: true, description: "Structured pricing tiers with `name`, `price`, `features`, `ctaText`, and `isPopular`. Legacy flat tier props remain readable on load." },
    ],
    normalizeForRender: normalizePricingTableProps,
  },
  {
    type: "video-embed",
    editorKey: "VideoEmbed",
    label: "Video Embed",
    description: "Responsive video embed preview for YouTube, Vimeo, or direct video URLs.",
    usageGuidance: "Use for VSLs, product demos, and testimonial videos.",
    props: [
      { name: "url", type: "string", required: true, description: "YouTube, Vimeo, or direct video URL." },
      { name: "caption", type: "string", required: false, description: "Caption shown below the video." },
    ],
    normalizeForRender: normalizeVideoEmbedProps,
  },
  {
    type: "countdown-timer",
    editorKey: "CountdownTimer",
    label: "Countdown Timer",
    description: "Urgency timer showing days, hours, minutes, and seconds until a deadline.",
    usageGuidance: "Use only for genuine deadlines such as webinar start times or expiring offers. Webinar pages should carry an explicit webinar page config so the deadline source stays truthful.",
    props: [
      { name: "heading", type: "string", required: false, description: "Text above the timer." },
      { name: "deadline", type: "string", required: true, description: "ISO 8601 deadline." },
      { name: "expiredMessage", type: "string", required: false, description: "Message shown when the timer reaches zero.", defaultValue: "This offer has expired." },
      { name: "variant", type: "'inline' | 'banner'", required: false, description: "Display style.", defaultValue: "inline" },
    ],
  },
  {
    type: "calendar-embed",
    editorKey: "calendar-embed",
    label: "Calendar Embed",
    description: "Service-delivery booking calendar wired to real tenant availability.",
    usageGuidance: "Use on booking and consultation pages. Prefer serviceId and reuse the same public service contract as booking-widget.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "Book Your Appointment" },
      { name: "action", type: "BlockAction", required: false, description: "Canonical typed booking action." },
      { name: "serviceId", type: "string", required: false, description: "Canonical public service ID from the tenant workspace." },
      { name: "calendarId", type: "string", required: false, description: "Deprecated legacy alias for serviceId." },
      { name: "duration", type: "number", required: false, description: "Default appointment duration in minutes.", defaultValue: "30" },
      { name: "bookingUrl", type: "string", required: false, description: "Fallback booking URL." },
      { name: "showTimezone", type: "'yes' | 'no'", required: false, description: "Show timezone label.", defaultValue: "yes" },
    ],
    actionContract: {
      propName: "action",
      supportedKinds: BOOKING_ACTION_KINDS,
      legacyUrlProp: "bookingUrl",
    },
    normalizeForRender: normalizeServiceBookingProps,
  },
  {
    type: "faq-accordion",
    editorKey: "FaqAccordion",
    label: "FAQ Accordion",
    description: "Expandable FAQ section edited as structured question and answer items.",
    usageGuidance: "Place near the bottom to handle objections and improve AEO coverage.",
    props: [
      { name: "items", type: "FaqItem[]", required: true, description: "Structured FAQ items with `question` and `answer`. Legacy pipe-delimited textarea values are still accepted on read." },
    ],
    normalizeForRender: normalizeFaqProps,
  },
  {
    type: "image-gallery",
    editorKey: "Gallery",
    label: "Image Gallery",
    description: "Responsive image gallery edited as structured image records.",
    usageGuidance: "Use for portfolios, product photos, or before/after comparisons.",
    props: [
      { name: "images", type: "GalleryItem[]", required: true, description: "Structured gallery items with `src`, `alt`, and `caption`. Legacy line-based image URLs remain readable on load." },
      { name: "columns", type: "'2' | '3' | '4'", required: false, description: "Number of columns.", defaultValue: "3" },
    ],
    normalizeForRender: normalizeGalleryProps,
  },
  {
    type: "social-proof",
    editorKey: "SocialProof",
    label: "Social Proof",
    description: "Stats, logo, or badge-based credibility strip with builder-friendly textarea editing.",
    usageGuidance: "Place below the hero or before the main CTA to build trust early.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading." },
      { name: "variant", type: "'stats' | 'logos' | 'badges'", required: true, description: "Display variant." },
      { name: "items", type: "textarea", required: true, description: "One item per line: `Value | Label`." },
    ],
    normalizeForRender: normalizeSocialProofProps,
  },
  {
    type: "navigation-bar",
    editorKey: "NavigationBar",
    label: "Navigation Bar",
    description: "Top navigation with logo, links, and optional CTA button.",
    usageGuidance: "Optional for landing pages. Use sparingly to avoid distracting exits.",
    props: [
      { name: "logoUrl", type: "string", required: false, description: "Logo image URL." },
      { name: "linkList", type: "textarea", required: false, description: "One link per line: `Label | URL`." },
      { name: "ctaText", type: "string", required: false, description: "CTA button label." },
      { name: "ctaUrl", type: "string", required: false, description: "CTA button link." },
      { name: "sticky", type: "'yes' | 'no'", required: false, description: "Stick to the top on scroll.", defaultValue: "yes" },
    ],
    normalizeForRender: normalizeNavigationBarProps,
  },
  {
    type: "popup-trigger",
    editorKey: "popup-trigger",
    label: "Popup / Exit Intent",
    description: "Overlay popup triggered by exit intent, scroll depth, or time delay.",
    usageGuidance: "Use for exit intent offers or delayed promotions. Keep the CTA on the canonical action contract.",
    props: [
      { name: "trigger", type: "'exit_intent' | 'scroll_depth' | 'time_delay'", required: true, description: "Popup trigger type." },
      { name: "scrollPercentage", type: "number", required: false, description: "Trigger percentage for scroll depth.", defaultValue: "50" },
      { name: "delaySeconds", type: "number", required: false, description: "Delay before showing the popup.", defaultValue: "10" },
      { name: "heading", type: "string", required: true, description: "Popup headline." },
      { name: "content", type: "string", required: false, description: "Popup body text." },
      { name: "ctaText", type: "string", required: true, description: "Popup CTA button label." },
      { name: "action", type: "BlockAction", required: false, description: "Canonical typed CTA action." },
      { name: "ctaUrl", type: "string", required: false, description: "Legacy popup CTA URL.", defaultValue: "#form" },
      { name: "showOnce", type: "boolean", required: false, description: "Show only once per session.", defaultValue: "true" },
    ],
    actionContract: {
      propName: "action",
      supportedKinds: ALL_PRIMARY_ACTION_KINDS,
      legacyUrlProp: "ctaUrl",
    },
    normalizeForRender: normalizePopupTriggerProps,
  },
  {
    type: "tracking-pixel",
    editorKey: "tracking-pixel",
    label: "Tracking Pixel",
    description: "Invisible tracking component that fires custom analytics events on page load.",
    usageGuidance: "Use for advanced tracking cases beyond the default pageview.",
    props: [
      { name: "eventName", type: "string", required: true, description: "Custom event name to fire." },
      { name: "platforms", type: "('posthog' | 'meta' | 'google' | 'tiktok')[]", required: true, description: "Platforms that should receive the event." },
      { name: "eventData", type: "Record<string, string>", required: false, description: "Additional event properties." },
    ],
  },
  {
    type: "blog-post-card",
    editorKey: "blog-post-card",
    label: "Blog Post Card",
    description: "Single blog post preview card with image, title, excerpt, and metadata.",
    usageGuidance: "Use to feature individual posts on homepages or content pages.",
    props: [
      { name: "title", type: "string", required: true, description: "Post title." },
      { name: "excerpt", type: "string", required: false, description: "Short post summary." },
      { name: "featuredImage", type: "string", required: false, description: "Featured image URL." },
      { name: "publishedAt", type: "string", required: false, description: "ISO 8601 publish date." },
      { name: "authorName", type: "string", required: false, description: "Author display name." },
      { name: "category", type: "string", required: false, description: "Category label." },
      { name: "slug", type: "string", required: true, description: "URL slug for the post link." },
      { name: "readingTimeMinutes", type: "number", required: false, description: "Estimated reading time in minutes." },
      { name: "ctaText", type: "string", required: false, description: "Read-more link text.", defaultValue: "Read More" },
    ],
  },
  {
    type: "blog-listing-grid",
    editorKey: "blog-listing-grid",
    label: "Blog Listing Grid",
    description: "Responsive grid of blog post cards with configurable columns.",
    usageGuidance: "Use on blog index pages or homepages. Generate 3-6 posts for a balanced layout.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "Latest Posts" },
      { name: "posts", type: "BlogPost[]", required: true, description: "Blog post card payloads." },
      { name: "columns", type: "1 | 2 | 3", required: false, description: "Number of columns.", defaultValue: "3" },
      { name: "showCategory", type: "boolean", required: false, description: "Show category badge.", defaultValue: "true" },
      { name: "showAuthor", type: "boolean", required: false, description: "Show author name.", defaultValue: "true" },
      { name: "ctaText", type: "string", required: false, description: "Read-more link text.", defaultValue: "Read More" },
      { name: "baseUrl", type: "string", required: false, description: "Base URL prefix for links.", defaultValue: "/blog" },
    ],
  },
  {
    type: "order-bump",
    editorKey: "order-bump",
    label: "Order Bump",
    description: "Checkbox add-on offer for funnel checkout pages.",
    usageGuidance: "Use only inside funnels to increase average order value. The block is truthful only when it is bound to a real funnel submission surface via binding.",
    props: [
      { name: "productId", type: "string", required: true, description: "Product identifier for the bump offer." },
      { name: "headline", type: "string", required: true, description: "Offer headline." },
      { name: "description", type: "string", required: true, description: "Short description of the add-on." },
      { name: "originalPrice", type: "string", required: false, description: "Original strikethrough price." },
      { name: "bumpPrice", type: "string", required: true, description: "Discounted bump price." },
      { name: "checkboxLabel", type: "string", required: true, description: "Checkbox label." },
      { name: "imageUrl", type: "string", required: false, description: "Optional product image URL." },
      { name: "binding", type: "OrderBumpBinding", required: false, description: "Typed funnel submission binding that carries the bump selection into the step's real form/checkout payload." },
    ],
  },
  {
    type: "booking-widget",
    editorKey: "BookingWidget",
    label: "Booking Widget",
    description: "Interactive service-delivery booking widget wired to real tenant availability.",
    usageGuidance: "Use for consultation, discovery call, and appointment pages. Prefer serviceId as the canonical booking contract.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading." },
      { name: "action", type: "BlockAction", required: false, description: "Canonical typed booking action." },
      { name: "serviceId", type: "string", required: false, description: "Canonical public service ID from the tenant workspace." },
      { name: "calendarId", type: "string", required: false, description: "Deprecated legacy alias for serviceId." },
      { name: "duration", type: "number", required: false, description: "Appointment duration in minutes.", defaultValue: "30" },
      { name: "bookingUrl", type: "string", required: false, description: "Fallback booking URL." },
      { name: "showTimezone", type: "'yes' | 'no'", required: false, description: "Show timezone label.", defaultValue: "yes" },
    ],
    actionContract: {
      propName: "action",
      supportedKinds: BOOKING_ACTION_KINDS,
      legacyUrlProp: "bookingUrl",
    },
    normalizeForRender: normalizeServiceBookingProps,
  },
  {
    type: "feature-grid",
    editorKey: "FeatureGrid",
    label: "Feature Grid",
    description: "Feature-benefit grid edited as structured feature cards in the builder.",
    usageGuidance: "Use after the hero to explain core product value.",
    props: [
      { name: "features", type: "FeatureItem[]", required: true, description: "Structured feature items with `title` and `description`. Legacy pipe-delimited textarea values are still accepted on read." },
      { name: "columns", type: "'2' | '3'", required: false, description: "Number of columns.", defaultValue: "3" },
    ],
    normalizeForRender: normalizeFeatureGridProps,
  },
  {
    type: "logo-cloud",
    editorKey: "LogoCloud",
    label: "Logo Cloud",
    description: "Partner-logo showcase with generated placeholders in the builder.",
    usageGuidance: "Place below the hero or after testimonials to reinforce trust.",
    props: [
      { name: "heading", type: "string", required: false, description: "Optional heading." },
      { name: "logoCount", type: "number", required: true, description: "Number of placeholder logos to show.", defaultValue: "5" },
      { name: "size", type: "'sm' | 'md' | 'lg'", required: false, description: "Logo size.", defaultValue: "md" },
      { name: "grayscale", type: "'yes' | 'no'", required: false, description: "Display logos in grayscale.", defaultValue: "yes" },
    ],
    normalizeForRender: normalizeLogoCloudProps,
  },
  {
    type: "stats-counter",
    editorKey: "StatsCounter",
    label: "Stats Counter",
    description: "Key metric section edited as structured stat entries in the builder.",
    usageGuidance: "Use to show 3-4 high-signal stats that reinforce credibility.",
    props: [
      { name: "stats", type: "StatItem[]", required: true, description: "Structured stat items with `number` and `label`. Legacy pipe-delimited textarea values are still accepted on read." },
    ],
    normalizeForRender: normalizeStatsCounterProps,
  },
  {
    type: "comparison-table",
    editorKey: "ComparisonTable",
    label: "Comparison Table",
    description: "Feature comparison table edited as plain text rows and columns.",
    usageGuidance: "Use on pricing or competitive-comparison pages.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "Compare Plans" },
      { name: "columns", type: "textarea", required: true, description: "One column per line. Prefix `*` to highlight a column." },
      { name: "rows", type: "textarea", required: true, description: "One row per line: `Feature | value1 | value2 | ...`." },
      { name: "ctaText", type: "string", required: false, description: "CTA button text." },
      { name: "action", type: "BlockAction", required: false, description: "Canonical typed comparison CTA action." },
      { name: "ctaUrl", type: "string", required: false, description: "CTA button URL.", defaultValue: "#form" },
    ],
    actionContract: {
      propName: "action",
      supportedKinds: ALL_PRIMARY_ACTION_KINDS,
      legacyUrlProp: "ctaUrl",
    },
    normalizeForRender: normalizeComparisonTableProps,
  },
  {
    type: "roi-calculator",
    editorKey: "RoiCalculator",
    label: "ROI Calculator",
    description: "Interactive two-slider ROI estimator that recalculates a fixed formula without React on the published page.",
    usageGuidance: "Use near pricing or offer sections to help buyers quantify the outcome before the CTA.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "Estimate the upside" },
      { name: "description", type: "string", required: false, description: "Support copy above the sliders." },
      { name: "primaryLabel", type: "string", required: true, description: "Label for the first slider." },
      { name: "primaryMin", type: "number", required: true, description: "Minimum first-slider value.", defaultValue: "10" },
      { name: "primaryMax", type: "number", required: true, description: "Maximum first-slider value.", defaultValue: "500" },
      { name: "primaryDefault", type: "number", required: true, description: "Default first-slider value.", defaultValue: "120" },
      { name: "primaryStep", type: "number", required: false, description: "First-slider step increment.", defaultValue: "10" },
      { name: "primaryPrefix", type: "string", required: false, description: "Prefix shown with the first slider value." },
      { name: "primarySuffix", type: "string", required: false, description: "Suffix shown with the first slider value." },
      { name: "secondaryLabel", type: "string", required: true, description: "Label for the second slider." },
      { name: "secondaryMin", type: "number", required: true, description: "Minimum second-slider value.", defaultValue: "100" },
      { name: "secondaryMax", type: "number", required: true, description: "Maximum second-slider value.", defaultValue: "5000" },
      { name: "secondaryDefault", type: "number", required: true, description: "Default second-slider value.", defaultValue: "850" },
      { name: "secondaryStep", type: "number", required: false, description: "Second-slider step increment.", defaultValue: "50" },
      { name: "secondaryPrefix", type: "string", required: false, description: "Prefix shown with the second slider value." },
      { name: "secondarySuffix", type: "string", required: false, description: "Suffix shown with the second slider value." },
      { name: "formula", type: "'multiply' | 'percentage' | 'compound'", required: true, description: "Fixed calculator formula.", defaultValue: "multiply" },
      { name: "multiplier", type: "number", required: true, description: "Formula multiplier or compounding periods.", defaultValue: "12" },
      { name: "resultLabel", type: "string", required: true, description: "Label for the computed result." },
      { name: "resultPrefix", type: "string", required: false, description: "Prefix shown with the computed result." },
      { name: "resultSuffix", type: "string", required: false, description: "Suffix shown with the computed result." },
      { name: "ctaText", type: "string", required: false, description: "Optional CTA text." },
      { name: "action", type: "BlockAction", required: false, description: "Canonical typed CTA action." },
      { name: "ctaUrl", type: "string", required: false, description: "Optional CTA URL.", defaultValue: "#form" },
    ],
    actionContract: {
      propName: "action",
      supportedKinds: ALL_PRIMARY_ACTION_KINDS,
      legacyUrlProp: "ctaUrl",
    },
    normalizeForRender: normalizeRoiCalculatorProps,
  },
  {
    type: "social-proof-popup",
    editorKey: "SocialProofPopup",
    label: "Social Proof Popup",
    description: "Session-aware visitor proof popup that cycles recent activity-style notifications on published pages.",
    usageGuidance: "Use sparingly on landing pages where lightweight proof nudges should support, not interrupt, the main CTA.",
    props: [
      { name: "notifications", type: "SocialProofPopupItem[]", required: true, description: "Structured notification records with `name`, `location`, `action`, and `timeAgo`." },
      { name: "displayInterval", type: "number", required: false, description: "Seconds to wait before showing the next notification.", defaultValue: "10" },
      { name: "displayDuration", type: "number", required: false, description: "Seconds each notification stays visible.", defaultValue: "4" },
      { name: "position", type: "'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'", required: false, description: "Corner placement for the popup.", defaultValue: "bottom-left" },
      { name: "showOnMobile", type: "'yes' | 'no'", required: false, description: "Whether to show the popup on narrow screens.", defaultValue: "yes" },
      { name: "maxShown", type: "number", required: false, description: "Maximum number of notifications to show in a session.", defaultValue: "5" },
      { name: "randomize", type: "'yes' | 'no'", required: false, description: "Randomize notification order before cycling.", defaultValue: "no" },
    ],
    normalizeForRender: normalizeSocialProofPopupProps,
  },
  {
    type: "before-after",
    editorKey: "BeforeAfter",
    label: "Before / After",
    description: "Interactive image comparison slider for transformation proofs and visual reveals.",
    usageGuidance: "Use for renovation, treatment, design, or optimization stories where a drag reveal increases trust.",
    props: [
      { name: "beforeImage", type: "string", required: false, description: "Image shown underneath the comparison reveal." },
      { name: "afterImage", type: "string", required: false, description: "Image revealed by moving the slider." },
      { name: "beforeLabel", type: "string", required: false, description: "Badge label for the base image.", defaultValue: "Before" },
      { name: "afterLabel", type: "string", required: false, description: "Badge label for the revealed image.", defaultValue: "After" },
      { name: "initialPosition", type: "number", required: false, description: "Initial reveal percentage from 0-100.", defaultValue: "50" },
    ],
  },
  {
    type: "embed-widget",
    editorKey: "EmbedWidget",
    label: "Embed Widget",
    description: "Sandboxed iframe embed for trusted third-party tools.",
    usageGuidance: "Use for Calendly, Typeform, Maps, Loom, and similar trusted embeds.",
    props: [
      { name: "heading", type: "string", required: false, description: "Optional heading above the embed." },
      { name: "embedUrl", type: "string", required: true, description: "URL to embed. Must be https." },
      { name: "title", type: "string", required: true, description: "Accessible iframe title." },
      { name: "height", type: "string", required: false, description: "Iframe height.", defaultValue: "400px" },
      { name: "aspectRatio", type: "'auto' | '16:9' | '4:3' | '1:1'", required: false, description: "Aspect ratio constraint.", defaultValue: "auto" },
    ],
    normalizeForRender: normalizeEmbedWidgetProps,
  },
  {
    type: "video-player",
    editorKey: "VideoPlayer",
    label: "Video Player",
    description: "Course lesson video block with progress-aware playback settings.",
    usageGuidance: "Use inside course lessons for hosted or embedded lesson videos. Completion should reflect the actual viewing threshold.",
    props: [
      { name: "videoUrl", type: "string", required: true, description: "Video URL or embed URL." },
      { name: "sourceType", type: "'youtube' | 'vimeo' | 'bunny' | 'wistia' | 'loom' | 'r2' | 'auto'", required: false, description: "Optional source override.", defaultValue: "auto" },
      { name: "title", type: "string", required: false, description: "Accessible title shown above the video." },
      { name: "showProgress", type: "'yes' | 'no'", required: false, description: "Show the lesson progress indicator.", defaultValue: "yes" },
      { name: "completionThreshold", type: "number", required: false, description: "Percent watched required for completion.", defaultValue: "90" },
      { name: "autoPlay", type: "'yes' | 'no'", required: false, description: "Auto-play when supported.", defaultValue: "no" },
      { name: "posterUrl", type: "string", required: false, description: "Poster image URL for direct videos." },
    ],
    normalizeForRender: normalizeVideoPlayerProps,
  },
  {
    type: "lottie-player",
    editorKey: "LottiePlayer",
    label: "Lottie Player",
    description: "JSON-driven motion block for lightweight product, hero, and micro-interaction animation.",
    usageGuidance: "Use sparingly for motion accents that reinforce the page story. Upload tenant-scoped JSON or paste a trusted public URL.",
    props: [
      { name: "source", type: "string", required: true, description: "Absolute or site-rewritten URL to the Lottie JSON payload." },
      { name: "autoplay", type: "'yes' | 'no'", required: false, description: "Auto-play when no trigger overrides it.", defaultValue: "yes" },
      { name: "loop", type: "'yes' | 'no'", required: false, description: "Repeat the animation after it finishes.", defaultValue: "yes" },
      { name: "speed", type: "number", required: false, description: "Playback speed from 0.1 to 5x.", defaultValue: "1" },
      { name: "trigger", type: "'none' | 'hover' | 'scroll-into-view' | 'click'", required: false, description: "Interaction that starts playback.", defaultValue: "none" },
      { name: "width", type: "string", required: false, description: "Rendered width CSS value.", defaultValue: "100%" },
      { name: "height", type: "string", required: false, description: "Rendered height CSS value.", defaultValue: "auto" },
    ],
  },
  {
    type: "progress-bar",
    editorKey: "ProgressBar",
    label: "Progress Bar",
    description: "Course progress indicator for lessons, modules, or full-course completion.",
    usageGuidance: "Use near the top or bottom of a lesson page to reinforce progress through the course path.",
    props: [
      { name: "label", type: "string", required: false, description: "Label shown next to the progress state.", defaultValue: "Your Progress" },
      { name: "trackingScope", type: "'lesson' | 'module' | 'course' | 'custom'", required: true, description: "Progress scope this bar represents.", defaultValue: "module" },
      { name: "currentPercent", type: "number", required: false, description: "Current completion percentage.", defaultValue: "0" },
      { name: "showPercent", type: "'yes' | 'no'", required: false, description: "Show the numeric percentage.", defaultValue: "yes" },
      { name: "progressColor", type: "string", required: false, description: "Bar color while progress is incomplete." },
      { name: "completeColor", type: "string", required: false, description: "Bar color once progress reaches 100%." },
    ],
    normalizeForRender: normalizeProgressBarProps,
  },
  {
    type: "quiz-block",
    editorKey: "QuizBlock",
    label: "Quiz Block",
    description: "Scored course quiz block with pass threshold, attempts, and optional explanations.",
    usageGuidance: "Use for knowledge checks. Keep questions concise and align the passing score with the course completion rules.",
    props: [
      { name: "title", type: "string", required: true, description: "Quiz heading." },
      { name: "description", type: "string", required: false, description: "Optional quiz intro copy." },
      { name: "questionList", type: "textarea", required: true, description: "One question per line: `Question | Option A | Option B | Option C | Option D | 1` where the final value is the correct option index or exact option text." },
      { name: "passingScore", type: "number", required: true, description: "Minimum score required to pass.", defaultValue: "70" },
      { name: "maxAttempts", type: "number", required: false, description: "Maximum attempts allowed.", defaultValue: "3" },
      { name: "shuffleQuestions", type: "'yes' | 'no'", required: false, description: "Shuffle the question order.", defaultValue: "no" },
      { name: "shuffleOptions", type: "'yes' | 'no'", required: false, description: "Shuffle option order.", defaultValue: "no" },
      { name: "showExplanations", type: "'yes' | 'no'", required: false, description: "Show answer explanations after submission.", defaultValue: "yes" },
      { name: "timeLimitMinutes", type: "number", required: false, description: "Optional time limit in minutes." },
    ],
    normalizeForRender: normalizeQuizBlockProps,
  },
  {
    type: "exercise-block",
    editorKey: "ExerciseBlock",
    label: "Exercise Block",
    description: "Exercise submission block for course assignments and practice work.",
    usageGuidance: "Use when a student must submit work or evidence before progression or review.",
    props: [
      { name: "title", type: "string", required: true, description: "Exercise title." },
      { name: "instructions", type: "string", required: true, description: "Student-facing exercise instructions." },
      { name: "acceptedFileTypes", type: "textarea", required: false, description: "Comma-separated file extensions accepted for upload.", defaultValue: ".pdf,.doc,.docx,.jpg,.png,.zip" },
      { name: "maxFileSizeMb", type: "number", required: false, description: "Maximum upload size in MB.", defaultValue: "50" },
      { name: "required", type: "'yes' | 'no'", required: false, description: "Require submission before completion.", defaultValue: "yes" },
      { name: "rubric", type: "string", required: false, description: "Optional rubric or grading criteria." },
    ],
    normalizeForRender: normalizeExerciseBlockProps,
  },
  {
    type: "drip-gate",
    editorKey: "DripGate",
    label: "Drip Gate",
    description: "Conditional gate that unlocks the next course section based on date, delay, progress, or quiz score.",
    usageGuidance: "Place directly before gated content. The gate should communicate exactly what the learner needs to unlock next.",
    props: [
      { name: "mode", type: "'immediately' | 'date_based' | 'days_after_enrollment' | 'progress_based' | 'quiz_score_based'", required: true, description: "Unlock strategy." },
      { name: "unlockDate", type: "string", required: false, description: "ISO date used when mode is `date_based`." },
      { name: "delayDays", type: "number", required: false, description: "Delay in days after enrollment." },
      { name: "prerequisiteId", type: "string", required: false, description: "Required lesson or module ID for progress-based gating." },
      { name: "prerequisiteType", type: "'module' | 'lesson'", required: false, description: "Whether the prerequisite ID is a module or lesson.", defaultValue: "lesson" },
      { name: "requiredQuizId", type: "string", required: false, description: "Quiz ID used for score-based gating." },
      { name: "requiredScore", type: "number", required: false, description: "Minimum quiz score required.", defaultValue: "70" },
      { name: "lockedMessage", type: "string", required: false, description: "Message shown while locked." },
      { name: "unlockedMessage", type: "string", required: false, description: "Message shown when unlocked." },
    ],
    normalizeForRender: normalizeDripGateProps,
  },
  {
    type: "chat-widget",
    editorKey: "ChatWidget",
    label: "Chat Widget",
    description: "Floating live-chat widget for visitor messaging.",
    usageGuidance: "Use on published pages where live messaging should be available.",
    props: [
      { name: "greeting", type: "string", required: false, description: "Initial greeting message.", defaultValue: "Hi! How can we help?" },
      { name: "action", type: "BlockAction", required: false, description: "Canonical typed chat action for truthful mounted chat flows." },
      { name: "placeholder", type: "string", required: false, description: "Input placeholder.", defaultValue: "Type a message..." },
      { name: "position", type: "'br' | 'bl'", required: false, description: "Bottom-right or bottom-left placement.", defaultValue: "br" },
      { name: "aiEnabled", type: "'yes' | 'no'", required: false, description: "Enable AI auto-reply.", defaultValue: "yes" },
      { name: "offlineMessage", type: "string", required: false, description: "Message shown when no agents are online.", defaultValue: "We'll get back to you soon." },
      { name: "collectName", type: "'yes' | 'no'", required: false, description: "Collect visitor name before chat.", defaultValue: "yes" },
    ],
    actionContract: {
      propName: "action",
      supportedKinds: CHAT_ACTION_KINDS,
    },
    normalizeForRender: normalizeChatWidgetProps,
  },
  {
    type: "heading",
    editorKey: "Heading",
    label: "Heading",
    description: "Standalone heading element (H1-H6) for section titles and page headers.",
    usageGuidance: "Use for standalone section titles. Prefer hero-section for the page's main H1 above the fold.",
    props: [
      { name: "text", type: "string", required: true, description: "Heading text content.", defaultValue: "Your heading here" },
      { name: "level", type: "'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'", required: false, description: "Semantic heading level.", defaultValue: "h2" },
      { name: "align", type: "'left' | 'center' | 'right'", required: false, description: "Text alignment.", defaultValue: "left" },
    ],
  },
  {
    type: "text",
    editorKey: "Text",
    label: "Text",
    description: "Paragraph text block. Blank lines split content into separate paragraphs.",
    usageGuidance: "Use for short body copy between richer blocks. For long-form copy with its own heading, prefer text-block.",
    props: [
      { name: "content", type: "textarea", required: true, description: "Paragraph content. Blank lines create separate paragraphs.", defaultValue: "Your text content here." },
      { name: "align", type: "'left' | 'center' | 'right'", required: false, description: "Text alignment.", defaultValue: "left" },
    ],
  },
  {
    type: "image",
    editorKey: "Image",
    label: "Image",
    description: "Standalone responsive image with optional caption.",
    usageGuidance: "Use for single decorative or illustrative images. For multi-image layouts, prefer image-gallery.",
    props: [
      { name: "src", type: "string", required: true, description: "Image URL." },
      { name: "alt", type: "string", required: true, description: "Accessible alt text describing the image.", defaultValue: "Image description" },
      { name: "caption", type: "string", required: false, description: "Optional caption rendered below the image." },
      { name: "maxWidth", type: "string", required: false, description: "Maximum rendered width (CSS value).", defaultValue: "100%" },
    ],
  },
  {
    type: "button",
    editorKey: "Button",
    label: "Button",
    description: "Standalone call-to-action button rendered as an inline anchor.",
    usageGuidance: "Use for inline links that need button styling. For full-width conversion strips, prefer cta-banner.",
    props: [
      { name: "label", type: "string", required: true, description: "Button label text.", defaultValue: "Click me" },
      { name: "url", type: "string", required: false, description: "Destination URL.", defaultValue: "#" },
      { name: "variant", type: "'primary' | 'secondary' | 'outline'", required: false, description: "Visual style variant.", defaultValue: "primary" },
    ],
  },
  {
    type: "divider",
    editorKey: "Divider",
    label: "Divider",
    description: "Horizontal separator line between sections.",
    usageGuidance: "Use to visually separate adjacent content sections. For purely structural whitespace, prefer spacer.",
    props: [
      { name: "variant", type: "'solid' | 'dashed' | 'dotted' | 'gradient'", required: false, description: "Line style.", defaultValue: "solid" },
      { name: "width", type: "string", required: false, description: "CSS width (e.g. 100%, 320px).", defaultValue: "100%" },
      { name: "margin", type: "string", required: false, description: "Vertical CSS margin (e.g. 24px, 1rem).", defaultValue: "24px" },
    ],
  },
  {
    type: "spacer",
    editorKey: "Spacer",
    label: "Spacer",
    description: "Empty vertical whitespace for spacing between sections.",
    usageGuidance: "Use for explicit breathing room between blocks. For a visible separator line, prefer divider.",
    props: [
      { name: "height", type: "string", required: false, description: "Spacer height in pixels (16/32/48/64/96/128).", defaultValue: "48" },
    ],
  },
  {
    type: "blockquote",
    editorKey: "Blockquote",
    label: "Blockquote",
    description: "Standalone pull quote with optional author and role attribution.",
    usageGuidance: "Use for a single editorial quote or callout. For multi-card customer proof, prefer testimonial-grid.",
    props: [
      { name: "quote", type: "string", required: true, description: "Quote body text.", defaultValue: "Your quote here." },
      { name: "author", type: "string", required: false, description: "Quote author name." },
      { name: "role", type: "string", required: false, description: "Author role or title." },
    ],
  },
  {
    type: "alert",
    editorKey: "Alert",
    label: "Alert",
    description: "Inline informational banner with severity variant.",
    usageGuidance: "Use for time-bounded notices (trial ending, maintenance, etc.). For persistent calls-to-action, prefer cta-banner.",
    props: [
      { name: "message", type: "string", required: true, description: "Alert message text.", defaultValue: "Your message here." },
      { name: "variant", type: "'info' | 'warning' | 'success' | 'error'", required: false, description: "Severity variant.", defaultValue: "info" },
    ],
  },
  {
    type: "newsletter",
    editorKey: "Newsletter",
    label: "Newsletter",
    description: "Inline newsletter signup form with email input and submit button.",
    usageGuidance: "Use for footer/in-page email capture. For richer multi-step capture, prefer lead-form or multi-step-form.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading text.", defaultValue: "Stay in the loop" },
      { name: "description", type: "string", required: false, description: "Optional supporting description." },
      { name: "buttonLabel", type: "string", required: false, description: "Submit button label.", defaultValue: "Subscribe" },
      { name: "placeholder", type: "string", required: false, description: "Email input placeholder.", defaultValue: "Enter your email" },
      { name: "action", type: "string", required: false, description: "Form POST endpoint. Omit to render a disabled form." },
    ],
  },
  // ---------------------------------------------------------------------
  // PB-E zone-bearing blocks
  // ---------------------------------------------------------------------
  {
    type: "container",
    editorKey: "Container",
    label: "Container",
    description: "Constrained vertical stack with optional padding and background. Accepts nested blocks via the `children` zone.",
    usageGuidance: "Use to bound width and group related blocks. Prefer SectionBlock when the group is a top-level page landmark.",
    props: [
      { name: "maxWidth", type: "string", required: false, description: "Max content width (px/rem/em/%/vw, or `auto`/`none`).", defaultValue: "1200px" },
      { name: "padding", type: "'none' | 'sm' | 'md' | 'lg' | 'xl'", required: false, description: "Vertical padding scale.", defaultValue: "md" },
      { name: "align", type: "'left' | 'center' | 'right'", required: false, description: "Text alignment for children.", defaultValue: "left" },
      { name: "background", type: "string", required: false, description: "Optional CSS color background (hex/rgb/hsl/named)." },
      { name: "children", type: "PuckComponent[]", required: false, description: "Nested blocks rendered inside the container zone." },
    ],
    hasZones: true,
    zoneNames: ["children"],
  },
  {
    type: "columns",
    editorKey: "Columns",
    label: "Columns",
    description: "Two- or three-column layout with per-column children zones.",
    usageGuidance: "Use for side-by-side content (e.g. feature + image, three pricing tiers). Falls back to a flat `children` array distributed round-robin.",
    props: [
      { name: "columns", type: "'2' | '3'", required: false, description: "Number of columns.", defaultValue: "2" },
      { name: "gap", type: "'sm' | 'md' | 'lg'", required: false, description: "Column gap scale.", defaultValue: "md" },
      { name: "columnContent", type: "Array<{ children: PuckComponent[] }>", required: false, description: "Per-column zones. Length should match `columns`." },
      { name: "children", type: "PuckComponent[]", required: false, description: "Legacy flat children — distributed round-robin if `columnContent` is missing." },
    ],
    hasZones: true,
    zoneNames: ["column-0", "column-1", "column-2"],
  },
  {
    type: "section-block",
    editorKey: "SectionBlock",
    label: "Section Block",
    description: "Full-width semantic section with optional title/subtitle preamble and a children zone.",
    usageGuidance: "Use as the canonical landmark for top-level page sections (Pricing, FAQ, Testimonials). Combine with Container/Columns inside for layout.",
    props: [
      { name: "title", type: "string", required: false, description: "Section title (rendered as h2/h3)." },
      { name: "subtitle", type: "string", required: false, description: "Optional supporting subtitle below the title." },
      { name: "headingLevel", type: "'h2' | 'h3'", required: false, description: "Heading level for the title.", defaultValue: "h2" },
      { name: "align", type: "'left' | 'center' | 'right'", required: false, description: "Header text alignment.", defaultValue: "left" },
      { name: "tone", type: "'default' | 'muted' | 'inverse'", required: false, description: "Visual tone variant.", defaultValue: "default" },
      { name: "anchorId", type: "string", required: false, description: "Optional anchor id for in-page links (alphanumeric + dash/underscore, ≤64 chars)." },
      { name: "children", type: "PuckComponent[]", required: false, description: "Nested blocks rendered inside the section body zone." },
    ],
    hasZones: true,
    zoneNames: ["children"],
  },
  // ---------------------------------------------------------------------
  // PB-F marketing primitives
  // ---------------------------------------------------------------------
  {
    type: "hero",
    editorKey: "Hero",
    label: "Hero",
    description: "Slim centred hero with title, subtitle, and a single CTA. The lightweight counterpart to hero-section.",
    usageGuidance: "Use on simple landing pages without background imagery. For full-bleed conversion banners with images and dual CTAs, prefer hero-section.",
    props: [
      { name: "title", type: "string", required: true, description: "Hero headline.", defaultValue: "Welcome" },
      { name: "subtitle", type: "string", required: false, description: "Optional supporting subtitle." },
      { name: "ctaLabel", type: "string", required: false, description: "CTA button label." },
      { name: "ctaUrl", type: "string", required: false, description: "CTA destination URL.", defaultValue: "#" },
    ],
  },
  {
    type: "feature-card",
    editorKey: "FeatureCard",
    label: "Feature Card",
    description: "Pipe-delimited feature tiles rendered as a responsive grid. Editor-friendly counterpart to feature-grid.",
    usageGuidance: "Use when authors edit features as a textarea (one `Title | Description` per line). For data-driven feature arrays, prefer feature-grid.",
    props: [
      { name: "items", type: "string", required: true, description: "Features (one per line: `Title | Description`).", defaultValue: "Title | Description" },
      { name: "columns", type: "'1' | '2' | '3' | '4'", required: false, description: "Number of grid columns.", defaultValue: "2" },
      { name: "variant", type: "'cards' | 'minimal'", required: false, description: "Visual variant.", defaultValue: "cards" },
    ],
  },
  {
    type: "icon-text",
    editorKey: "IconText",
    label: "Icon Text",
    description: "Single icon paired with a heading and description, for benefit lists.",
    usageGuidance: "Use inside Columns/Container for benefit checklists. The icon is rendered as escaped text — paste emoji or unicode glyphs, never raw HTML.",
    props: [
      { name: "icon", type: "string", required: false, description: "Icon glyph (emoji or unicode text)." },
      { name: "heading", type: "string", required: true, description: "Heading text.", defaultValue: "Benefit" },
      { name: "description", type: "string", required: false, description: "Supporting description." },
      { name: "layout", type: "'horizontal' | 'vertical'", required: false, description: "Layout direction.", defaultValue: "horizontal" },
    ],
  },
  {
    type: "client-logos",
    editorKey: "ClientLogos",
    label: "Client Logos",
    description: "Logo strip with optional heading. Every src is run through the same URL safelist used by the Image block.",
    usageGuidance: "Use for social-proof logo bands near the hero. For richer social-proof cards with quotes, prefer testimonial-grid.",
    props: [
      { name: "heading", type: "string", required: false, description: "Optional caption above the logo strip." },
      { name: "logos", type: "Array<{ src: string; alt?: string }>", required: true, description: "Logo image entries. Items without a usable src are dropped." },
    ],
  },
  {
    type: "awards",
    editorKey: "Awards",
    label: "Awards",
    description: "Award/recognition cards parsed from a pipe-delimited textarea (`Name | Year`).",
    usageGuidance: "Use for credibility strips (`Best SaaS Platform | 2026`). Lines without a name are dropped; year is optional.",
    props: [
      { name: "awards", type: "string", required: true, description: "Awards (one per line: `Name | Year`).", defaultValue: "Award | 2026" },
    ],
  },
  // ---------------------------------------------------------------------
  // PB-G engagement primitives
  // ---------------------------------------------------------------------
  {
    type: "timeline",
    editorKey: "Timeline",
    label: "Timeline",
    description: "Vertical chronological list parsed from a pipe-delimited textarea (`Date | Title | Description`).",
    usageGuidance: "Use for company milestones, roadmap history, or onboarding step recaps. Rows without a title are dropped; date and description are optional.",
    props: [
      { name: "events", type: "string", required: true, description: "Events (one per line: `Date | Title | Description`).", defaultValue: "2026 | Launch | Shipped the first release." },
    ],
  },
  {
    type: "code-block",
    editorKey: "CodeBlock",
    label: "Code Block",
    description: "Semantic <pre><code> snippet with an optional language label. Body is text-escaped, no client-side highlighter is shipped.",
    usageGuidance: "Use to embed short illustrative snippets (API examples, config). For long technical content, prefer a documentation page link.",
    props: [
      { name: "code", type: "string", required: true, description: "Snippet body.", defaultValue: "console.log('hello');" },
      { name: "language", type: "string", required: false, description: "Optional language label (rendered as plain text)." },
    ],
  },
  {
    type: "data-table",
    editorKey: "DataTable",
    label: "Data Table",
    description: "Semantic <table> built from a comma-separated header list and a textarea of comma-separated rows.",
    usageGuidance: "Use for plan comparisons and lightweight specs. Rows are normalised to the header count so screen-readers get a stable column shape.",
    props: [
      { name: "headers", type: "string", required: true, description: "Headers (comma-separated).", defaultValue: "Plan, Price" },
      { name: "rows", type: "string", required: true, description: "Rows (one per line, comma-separated cells).", defaultValue: "Starter, Free\nPro, 29/mo" },
    ],
  },
  {
    type: "tabs",
    editorKey: "Tabs",
    label: "Tabs",
    description: "Static accessible tab list parsed from a pipe-delimited textarea (`Title | Content`). Hydrated progressively by the tabs loader.",
    usageGuidance: "Use for short, comparable content groupings (Overview/Features/Pricing). Long content per tab should live on its own page.",
    props: [
      { name: "items", type: "string", required: true, description: "Tabs (one per line: `Title | Content`).", defaultValue: "Overview | Welcome.\nDetails | More info." },
    ],
  },
  {
    type: "social-icons",
    editorKey: "SocialIcons",
    label: "Social Icons",
    description: "Row of social media links parsed from a pipe-delimited textarea (`Platform | URL`). URLs are run through the URL safelist.",
    usageGuidance: "Use in footers and bios. Use the same URL safelist as the Image/Button blocks — `javascript:` URIs are coerced to `#`.",
    props: [
      { name: "icons", type: "string", required: true, description: "Links (one per line: `Platform | URL`).", defaultValue: "Twitter | https://twitter.com/ease" },
      { name: "size", type: "'sm' | 'md' | 'lg'", required: false, description: "Visual size.", defaultValue: "md" },
      { name: "style", type: "'filled' | 'outline' | 'plain'", required: false, description: "Visual style variant.", defaultValue: "filled" },
    ],
  },
  {
    type: "map-embed",
    editorKey: "MapEmbed",
    label: "Map Embed",
    description: "Static Google Maps iframe pointing at a `?q=<address>` search URL. No API key required.",
    usageGuidance: "Use on contact pages to show a single location. Pass a human-readable address; the published iframe is sandboxed and never executes operator JS.",
    props: [
      { name: "address", type: "string", required: true, description: "Address or place name passed to Google Maps search.", defaultValue: "Amsterdam, Netherlands" },
      { name: "heading", type: "string", required: false, description: "Optional heading above the map.", defaultValue: "Find Us" },
      { name: "height", type: "string", required: false, description: "Map height in `px` or `%` (digits only).", defaultValue: "300px" },
      { name: "showAddress", type: "'yes' | 'no'", required: false, description: "Render the address as a caption beneath the map.", defaultValue: "yes" },
    ],
  },
  {
    type: "video-background",
    editorKey: "VideoBackground",
    label: "Video Background",
    description: "Looping muted background video with a heading and subtitle overlay. Falls back to a gradient when no video is supplied.",
    usageGuidance: "Use as a hero on marketing pages. Only direct `https://…/file.mp4|webm|ogg|mov` URLs are accepted; everything else falls back to the gradient.",
    props: [
      { name: "videoUrl", type: "string", required: false, description: "Direct video URL (https + supported extension).", defaultValue: "" },
      { name: "heading", type: "string", required: false, description: "Overlay heading.", defaultValue: "Transform Your Business" },
      { name: "subtitle", type: "string", required: false, description: "Overlay subtitle.", defaultValue: "Join thousands of companies using our AI-powered platform." },
      { name: "overlayOpacity", type: "number", required: false, description: "Overlay opacity 0-100 (clamped).", defaultValue: "60" },
    ],
  },
  {
    type: "image-hotspot",
    editorKey: "ImageHotspot",
    label: "Image Hotspot",
    description: "Annotated image with numbered markers parsed from a pipe-delimited textarea (`X% | Y% | Label`). Captions are always visible.",
    usageGuidance: "Use to call out points on floor plans or product photos. Coordinates are clamped to 0-100 so typos can't escape the frame.",
    props: [
      { name: "imageUrl", type: "string", required: true, description: "Base image URL.", defaultValue: "" },
      { name: "hotspots", type: "string", required: false, description: "Hotspots, one per line: `X% | Y% | Label`.", defaultValue: "25 | 30 | Reception\n70 | 50 | Treatment Room" },
    ],
  },
  {
    type: "carousel",
    editorKey: "Carousel",
    label: "Carousel",
    description: "Horizontal scroll-snap image strip. Published output uses native CSS scroll-snap so it works without JavaScript.",
    usageGuidance: "Use for image galleries that should swipe horizontally on touch. Each slide takes 100% width of the viewport.",
    props: [
      { name: "images", type: "Array<{src: string; alt: string}>", required: true, description: "Slides. Items with empty `src` are skipped.", defaultValue: "[]" },
      { name: "height", type: "number", required: false, description: "Viewport height in pixels (clamped 120-1200).", defaultValue: "400" },
    ],
  },
  {
    type: "notification-bar",
    editorKey: "NotificationBar",
    label: "Notification Bar",
    description: "Top-of-page announcement banner with optional link and dismiss control. Variant drives the class hook (`info`, `success`, `warning`, `promo`).",
    usageGuidance: "Use sparingly — one per page max. Links go through the URL safelist; unknown schemes (e.g. `javascript:`) resolve to `#`.",
    props: [
      { name: "message", type: "string", required: true, description: "Announcement copy.", defaultValue: "" },
      { name: "linkText", type: "string", required: false, description: "Optional link label.", defaultValue: "" },
      { name: "linkUrl", type: "string", required: false, description: "Optional link target.", defaultValue: "" },
      { name: "variant", type: "'info' | 'success' | 'warning' | 'promo'", required: false, description: "Visual variant.", defaultValue: "info" },
      { name: "dismissible", type: "boolean", required: false, description: "Show a dismiss button.", defaultValue: "false" },
    ],
  },
  {
    type: "offer-card",
    editorKey: "OfferCard",
    label: "Offer Card",
    description: "Limited-time offer card with badge, heading, description, before/after pricing, and a single CTA.",
    usageGuidance: "Use to spotlight a single promotion. The CTA URL passes through the URL safelist — unknown schemes resolve to `#`.",
    props: [
      { name: "badge", type: "string", required: false, description: "Small badge label above the heading.", defaultValue: "Limited Time" },
      { name: "heading", type: "string", required: false, description: "Offer heading.", defaultValue: "Spring Launch Special" },
      { name: "description", type: "string", required: false, description: "Supporting body copy.", defaultValue: "" },
      { name: "originalPrice", type: "string", required: false, description: "Strikethrough original price.", defaultValue: "" },
      { name: "offerPrice", type: "string", required: false, description: "Discounted price displayed prominently.", defaultValue: "" },
      { name: "ctaText", type: "string", required: false, description: "CTA button label.", defaultValue: "Claim This Offer" },
      { name: "ctaUrl", type: "string", required: false, description: "CTA destination URL.", defaultValue: "#" },
    ],
  },
  {
    type: "gift-card",
    editorKey: "GiftCard",
    label: "Gift Card",
    description: "Display-only gift-card block listing predefined amount tiles plus a purchase CTA. Purchase behavior is wired in the storefront — published HTML is JS-free.",
    usageGuidance: "Use on landing pages that promote a digital gift card. Amounts are parsed from a comma-separated string and clamped to 8 entries.",
    props: [
      { name: "title", type: "string", required: false, description: "Section title above the amount tiles.", defaultValue: "Give the Gift of Wellness" },
      { name: "amounts", type: "string", required: false, description: "Comma-separated list of amounts (max 8).", defaultValue: "25, 50, 100, 200" },
      { name: "currency", type: "string", required: false, description: "Currency symbol prefix (max 4 chars).", defaultValue: "€" },
      { name: "buttonLabel", type: "string", required: false, description: "Purchase button label.", defaultValue: "Purchase Gift Card" },
    ],
  },
  {
    type: "coupon",
    editorKey: "Coupon",
    label: "Coupon",
    description: "Two-pane coupon card with discount badge on the left and code/description/expiry on the right.",
    usageGuidance: "Use to surface a single promo code. The `Copy` affordance is presentational only — clipboard behavior lives in the storefront app.",
    props: [
      { name: "code", type: "string", required: false, description: "Coupon code rendered inside `<code>`.", defaultValue: "LAUNCH30" },
      { name: "description", type: "string", required: false, description: "Plain-text description of the offer.", defaultValue: "Get 30% off your first 3 months" },
      { name: "discount", type: "string", required: false, description: "Bold discount badge text (e.g. `30% OFF`).", defaultValue: "30% OFF" },
      { name: "expiry", type: "string", required: false, description: "Plain-text expiry copy.", defaultValue: "Expires April 30, 2026" },
    ],
  },
  {
    type: "breadcrumb",
    editorKey: "Breadcrumb",
    label: "Breadcrumb",
    description: "Semantic `<nav>` containing an ordered list of crumbs parsed from a pipe-delimited textarea (`Label | URL`). The last crumb is marked `aria-current=\"page\"`.",
    usageGuidance: "Place near the top of secondary pages. Links route through the URL safelist; `javascript:` resolves to `#`.",
    props: [
      { name: "items", type: "string", required: true, description: "Crumbs, one per line: `Label | URL`.", defaultValue: "Home | /\nServices | /services" },
      { name: "separator", type: "string", required: false, description: "Visible separator (max 4 chars).", defaultValue: "/" },
    ],
  },
  {
    type: "back-to-top",
    editorKey: "BackToTop",
    label: "Back to Top",
    description: "Static in-page anchor to `#top` with optional label. Position class hook is `br` (bottom-right) or `bl` (bottom-left). Show-after-scroll behavior is delegated to theme JS via `data-ease-show-after-px`.",
    usageGuidance: "Place at the bottom of long pages. The anchor target (`#top`) should exist on the page — usually the document body or hero.",
    props: [
      { name: "label", type: "string", required: false, description: "Optional text label next to the arrow icon.", defaultValue: "" },
      { name: "showAfterPx", type: "number", required: false, description: "Scroll threshold consumed by theme JS (clamped 0-10000).", defaultValue: "300" },
      { name: "position", type: "'br' | 'bl'", required: false, description: "Visual position class hook.", defaultValue: "br" },
    ],
  },
  {
    type: "floating-cta",
    editorKey: "FloatingCta",
    label: "Floating CTA",
    description: "Persistent pill-shaped call-to-action anchored to a screen corner. Published as a single safelisted `<a>` element so it works without JavaScript.",
    usageGuidance: "Use to drive a single primary action without competing with page content. Unsupported `href` schemes (e.g. `javascript:`) resolve to `#`.",
    props: [
      { name: "label", type: "string", required: true, description: "Visible label.", defaultValue: "" },
      { name: "href", type: "string", required: false, description: "Click target.", defaultValue: "#" },
      { name: "position", type: "'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'", required: false, description: "Corner anchor.", defaultValue: "bottom-right" },
      { name: "variant", type: "'primary' | 'secondary' | 'accent'", required: false, description: "Visual variant.", defaultValue: "primary" },
      { name: "icon", type: "string", required: false, description: "Optional leading glyph.", defaultValue: "" },
      { name: "ariaLabel", type: "string", required: false, description: "Accessible name override.", defaultValue: "" },
    ],
  },
  {
    type: "language-switcher",
    editorKey: "LanguageSwitcher",
    label: "Language Switcher",
    description: "i18n locale picker parsed from a pipe-delimited textarea (`locale | Label | /path` per line). Locales matching BCP-47 emit `lang`/`hreflang` attributes.",
    usageGuidance: "Use on multilingual sites. Set `current` to the active BCP-47 code to mark that row with `aria-current` and drop its link.",
    props: [
      { name: "locales", type: "string", required: true, description: "Locales, one per line: `locale | Label | /path`.", defaultValue: "en | English | /en\nfr | Français | /fr" },
      { name: "current", type: "string", required: false, description: "Active locale code.", defaultValue: "" },
      { name: "ariaLabel", type: "string", required: false, description: "Accessible name for the nav.", defaultValue: "Language" },
    ],
  },
  {
    type: "sticky-section",
    editorKey: "StickySection",
    label: "Sticky Section",
    description: "Heading + body block that pins to the viewport edge while the visitor scrolls. Offsets and position are class-driven, never inline.",
    usageGuidance: "Use for in-page section labels or persistent context. For rich content use a Container/Section block instead.",
    props: [
      { name: "heading", type: "string", required: false, description: "Pinned heading.", defaultValue: "" },
      { name: "body", type: "string", required: false, description: "Pinned body copy (single paragraph).", defaultValue: "" },
      { name: "position", type: "'top' | 'bottom'", required: false, description: "Which edge to pin to.", defaultValue: "top" },
      { name: "offset", type: "'none' | 'small' | 'medium' | 'large'", required: false, description: "Distance from the pinned edge.", defaultValue: "none" },
    ],
  },
  {
    type: "scroll-triggered-cta",
    editorKey: "ScrollTriggeredCta",
    label: "Scroll-Triggered CTA",
    description: "CTA revealed by theme JS after the visitor scrolls past `triggerPx`. Published HTML is JS-free and visible by default; the data attributes drive the runtime decoration.",
    usageGuidance: "Use sparingly. Pair with a single conversion goal. `ctaUrl` runs through the URL safelist; unsupported schemes resolve to `#`.",
    props: [
      { name: "heading", type: "string", required: false, description: "Short heading copy.", defaultValue: "" },
      { name: "body", type: "string", required: false, description: "Supporting body copy.", defaultValue: "" },
      { name: "ctaLabel", type: "string", required: false, description: "CTA button label.", defaultValue: "" },
      { name: "ctaUrl", type: "string", required: false, description: "CTA destination URL.", defaultValue: "#" },
      { name: "triggerPx", type: "number", required: false, description: "Scroll threshold consumed by theme JS (clamped 0-10000).", defaultValue: "600" },
      { name: "mode", type: "'slide-up' | 'fade' | 'slide-down'", required: false, description: "Reveal animation class hook.", defaultValue: "slide-up" },
    ],
  },
  {
    type: "social-feed",
    editorKey: "SocialFeed",
    label: "Social Feed",
    description: "Read-only grid of recent social posts parsed from a pipe-delimited textarea (`platform | author | caption | postUrl | imageUrl`). No third-party iframes or trackers in published HTML.",
    usageGuidance: "Use to mirror a curated social feed without embedding scripts. Platform names outside the safelist render under a generic class.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "" },
      { name: "posts", type: "string", required: true, description: "Posts, one per line: `platform | author | caption | postUrl | imageUrl`.", defaultValue: "instagram | @stero | Latest update | https://instagram.com/stero | " },
      { name: "columns", type: "number", required: false, description: "Grid columns (clamped 1-6).", defaultValue: "3" },
    ],
  },
  {
    type: "button-group",
    editorKey: "ButtonGroup",
    label: "Button Group",
    description: "Horizontal or stacked cluster of CTA links parsed from a pipe-delimited textarea (`Label | URL | variant` per line). Variants are constrained to a safelist (primary/secondary/outline) so CSS owns styling.",
    usageGuidance: "Use to present 2-5 parallel calls-to-action. Hrefs run through the URL safelist; `javascript:` resolves to `#`. For a single CTA use the Button block instead.",
    props: [
      { name: "buttons", type: "string", required: true, description: "Buttons, one per line: `Label | URL | variant`.", defaultValue: "Primary action | / | primary\nSecondary action | /about | secondary" },
      { name: "alignment", type: "'left' | 'center' | 'right'", required: false, description: "Horizontal alignment class hook.", defaultValue: "left" },
      { name: "orientation", type: "'horizontal' | 'vertical'", required: false, description: "Layout orientation.", defaultValue: "horizontal" },
      { name: "ariaLabel", type: "string", required: false, description: "Accessible name for the nav.", defaultValue: "Actions" },
    ],
  },
  {
    type: "services-list",
    editorKey: "ServicesList",
    label: "Services List",
    description: "Grid of service cards parsed from a pipe-delimited textarea (`Title | Description | Price | URL` per line). Price is treated as display text (escaped); commerce math belongs in dedicated pricing blocks.",
    usageGuidance: "Use on agency, photographer, and professional-services pages. URLs route through the URL safelist; cards without a URL render without a CTA link.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "Services" },
      { name: "services", type: "string", required: true, description: "Services, one per line: `Title | Description | Price | URL`.", defaultValue: "Consultation | 60-minute strategy session | $200 | /book\nFull project | End-to-end delivery | From $5,000 | /contact" },
      { name: "columns", type: "number", required: false, description: "Grid columns (clamped 1-4).", defaultValue: "3" },
      { name: "ctaLabel", type: "string", required: false, description: "CTA label shown on each card with a URL.", defaultValue: "Learn more" },
    ],
  },
  {
    type: "contact-info",
    editorKey: "ContactInfo",
    label: "Contact Info",
    description: "Contact card with optional organization, address, phone, email, and website. Phone and email use `tel:` / `mailto:` schemes; address is wrapped in `<address>` with a structured-data hook.",
    usageGuidance: "Use on contact pages, footers, and location landing pages. All fields are independently optional; the block renders only what the operator fills in.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "Contact us" },
      { name: "organization", type: "string", required: false, description: "Organization name.", defaultValue: "" },
      { name: "address", type: "string", required: false, description: "Full address (single line or multi-line).", defaultValue: "" },
      { name: "phone", type: "string", required: false, description: "Phone number (digits, +, -, spaces).", defaultValue: "" },
      { name: "email", type: "string", required: false, description: "Email address.", defaultValue: "" },
      { name: "website", type: "string", required: false, description: "Public website URL.", defaultValue: "" },
    ],
  },
  {
    type: "opening-hours",
    editorKey: "OpeningHours",
    label: "Opening Hours",
    description: "Semantic table of business hours parsed from a pipe-delimited textarea (`Day | Hours` per line). Day labels matching a safelist (mon/monday/tue/... etc.) emit stable `data-day` hooks for theme JS.",
    usageGuidance: "Use on contact pages, location pages, and footers. Use `Closed` (or leave the hours blank) for non-operating days. Times are display text — no parsing.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "Opening hours" },
      { name: "hours", type: "string", required: true, description: "Hours, one per line: `Day | Hours`.", defaultValue: "Mon | 9:00 - 17:00\nTue | 9:00 - 17:00\nWed | 9:00 - 17:00\nThu | 9:00 - 17:00\nFri | 9:00 - 17:00\nSat | Closed\nSun | Closed" },
      { name: "timezone", type: "string", required: false, description: "Display-only timezone label.", defaultValue: "" },
    ],
  },
  {
    type: "team-grid",
    editorKey: "TeamGrid",
    label: "Team Grid",
    description: "Grid of team member cards parsed from a pipe-delimited textarea (`Name | Role | Photo URL | Profile URL | Bio` per line). Photo sources use the hardened `imageUrl` validator; profile URLs use the standard safelist.",
    usageGuidance: "Use on About pages and agency landing pages. Photos are lazy-loaded; cards without a profile URL render the name as plain text.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "Our team" },
      { name: "members", type: "string", required: true, description: "Members, one per line: `Name | Role | Photo URL | Profile URL | Bio`.", defaultValue: "Jane Doe | Founder | | /team/jane | \nJohn Smith | Engineer | | /team/john | " },
      { name: "columns", type: "number", required: false, description: "Grid columns (clamped 1-4).", defaultValue: "3" },
    ],
  },
  {
    type: "rich-text",
    editorKey: "RichText",
    label: "Rich Text",
    description: "Long-form text block. `## ` and `### ` lines become `<h2>` / `<h3>` headings; `**bold**` and `*italic*` markers wrap to `<strong>` / `<em>`. Every text fragment is HTML-escaped before output — unbalanced markers degrade to plain text.",
    usageGuidance: "Use for body copy with light emphasis. Font size is constrained to sm/md/lg; max width accepts px/rem/em/% values. For SEO-heavy long-form blog content prefer the blog post components.",
    props: [
      { name: "content", type: "string", required: true, description: "Body text. One paragraph per line; prefix headings with `## ` or `### `.", defaultValue: "## About us\nWe help small businesses ship faster with **AI-native** workflows.\n\nEvery seat ships with the same tools the founders use." },
      { name: "maxWidth", type: "string", required: false, description: "Max content width (e.g. `720px`, `40rem`).", defaultValue: "720px" },
      { name: "fontSize", type: "'sm' | 'md' | 'lg'", required: false, description: "Type size class hook.", defaultValue: "md" },
    ],
  },
  {
    type: "accordion",
    editorKey: "Accordion",
    label: "Accordion",
    description: "Question / answer list rendered as native `<details>` and `<summary>` elements. Editor input is a pipe-delimited textarea (`Question | Answer` per line). The first item starts open so the block is not silent on first paint.",
    usageGuidance: "Use for short Q&A sets that don't need SEO schema. For schema.org FAQ pages use the `faq-accordion` block instead. When `allowMultiple` is `no` (default) the items form an exclusive group via the `name` attribute.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "Questions" },
      { name: "items", type: "string", required: true, description: "Items, one per line: `Question | Answer`.", defaultValue: "How do I start? | Sign up and follow the guided setup.\nIs there a free trial? | Yes — every account gets 14 days." },
      { name: "allowMultiple", type: "'yes' | 'no'", required: false, description: "Whether multiple items can be open at once.", defaultValue: "no" },
    ],
  },
  {
    type: "footer-block",
    editorKey: "FooterBlock",
    label: "Footer Block",
    description: "Multi-column site footer with company brand, tagline, link columns, and a copyright line. Columns are separated by `---` on its own line; each column starts with a title and is followed by `Label | URL` rows. URLs route through the URL safelist; `javascript:` collapses to `#`.",
    usageGuidance: "Use as the bottom-of-page surface on marketing sites. For a top-of-page announcement strip use `notification-bar`.",
    props: [
      { name: "companyName", type: "string", required: false, description: "Brand / company name.", defaultValue: "Your Company" },
      { name: "tagline", type: "string", required: false, description: "Short tagline shown under the brand.", defaultValue: "Run your business with ease." },
      { name: "columns", type: "string", required: false, description: "Link columns separated by `---`. Each column starts with a title line, then `Label | URL` rows.", defaultValue: "Product\nFeatures | /features\nPricing | /pricing\n---\nCompany\nAbout | /about\nContact | /contact" },
      { name: "copyright", type: "string", required: false, description: "Footer copyright line.", defaultValue: "2026 Your Company. All rights reserved." },
    ],
  },
  {
    type: "contact-form",
    editorKey: "ContactForm",
    label: "Contact Form",
    description: "Lightweight contact form with name, email, and message inputs plus a submit button. The form POSTs to the configured `action` URL via the URL safelist; an unsafe action collapses to `#`. Headings, button labels, and success messages are display text only.",
    usageGuidance: "Use for the standard 'Get in touch' surface. For richer lead capture with custom fields use `lead-form` or `multi-step-form`.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "Get in touch" },
      { name: "submitLabel", type: "string", required: false, description: "Submit button label.", defaultValue: "Send message" },
      { name: "successMessage", type: "string", required: false, description: "Message shown after a successful submit (read by the form runtime).", defaultValue: "Thanks! We'll be in touch soon." },
      { name: "action", type: "string", required: false, description: "Form POST endpoint. Routed through the URL safelist.", defaultValue: "" },
    ],
  },
  {
    type: "case-study",
    editorKey: "CaseStudy",
    label: "Case Study",
    description: "Single case-study card with company name, headline metric, and the Challenge / Solution / Result narrative triad. Missing sections drop out of the markup; an entirely empty block renders an HTML comment.",
    usageGuidance: "Use on social-proof pages and pitch decks. For a multi-card grid of proof points use `reviews-grid` or `social-proof`.",
    props: [
      { name: "company", type: "string", required: false, description: "Customer or company name.", defaultValue: "Bright Smiles Dental" },
      { name: "metric", type: "string", required: false, description: "Headline metric shown as a badge.", defaultValue: "+85% bookings" },
      { name: "challenge", type: "string", required: false, description: "What the customer was struggling with.", defaultValue: "Slow lead follow-up was costing 40% of inbound demand." },
      { name: "solution", type: "string", required: false, description: "What they did to fix it.", defaultValue: "Automated lead capture with AI-powered instant replies." },
      { name: "result", type: "string", required: false, description: "Measurable outcome.", defaultValue: "Response time dropped from 4 hours to 30 seconds." },
    ],
  },
  {
    type: "appointment-form",
    editorKey: "AppointmentForm",
    label: "Appointment Form",
    description: "Static booking form (name, email, service, date) for service-business pages. Service options come from a newline-delimited textarea; duplicates and blanks are dropped and the list is capped to 24 entries.",
    usageGuidance: "Use on service-business landing pages that need a lightweight intake form. Submission URL routes through the URL safelist; omit `action` (or supply a `javascript:` URL) to publish a disabled form rather than silently posting to `#`.",
    props: [
      { name: "heading", type: "string", required: false, description: "Form heading.", defaultValue: "Book Your Appointment" },
      { name: "services", type: "string", required: false, description: "Services, one per line.", defaultValue: "Initial Consultation\nFollow-up Visit\nPremium Treatment" },
      { name: "submitLabel", type: "string", required: false, description: "Submit button label.", defaultValue: "Book Now" },
      { name: "action", type: "string", required: false, description: "Form POST endpoint. Omit to render a disabled form.", defaultValue: "" },
      { name: "nameLabel", type: "string", required: false, description: "Override for the name field label.", defaultValue: "Full name" },
      { name: "emailLabel", type: "string", required: false, description: "Override for the email field label.", defaultValue: "Email address" },
    ],
  },
  {
    type: "waitlist-form",
    editorKey: "WaitlistForm",
    label: "Waitlist Form",
    description: "Static waitlist signup form with optional social-proof count. `currentCount` is clamped to a positive integer and formatted at render time, so a runaway value cannot break the layout.",
    usageGuidance: "Use on pre-launch landing pages or capacity-limited offers. Submission URL routes through the URL safelist; omit `action` (or supply a `javascript:` URL) to publish a disabled form rather than silently posting to `#`.",
    props: [
      { name: "heading", type: "string", required: false, description: "Form heading.", defaultValue: "Join the Waitlist" },
      { name: "description", type: "string", required: false, description: "Supporting copy below the heading.", defaultValue: "" },
      { name: "buttonLabel", type: "string", required: false, description: "Submit button label.", defaultValue: "Join Waitlist" },
      { name: "placeholder", type: "string", required: false, description: "Email input placeholder.", defaultValue: "Your email address" },
      { name: "action", type: "string", required: false, description: "Form POST endpoint. Omit to render a disabled form.", defaultValue: "" },
      { name: "currentCount", type: "number", required: false, description: "Optional current waitlist count for social proof. Clamped 0-100000000.", defaultValue: "" },
      { name: "countSuffix", type: "string", required: false, description: "Suffix appended to the count.", defaultValue: "people already on the list" },
    ],
  },
  {
    type: "membership-plans",
    editorKey: "MembershipPlans",
    label: "Membership Plans",
    description: "Grid of membership tiers parsed from a pipe-delimited textarea (`Name | Price | Features` per line, with features comma-separated). Plan count is capped to 6 and each plan's feature count to 12 so a typo cannot blow up the rendered grid.",
    usageGuidance: "Use on pricing pages for recurring memberships. CTAs route through the URL safelist; without a safe `ctaUrl` each card renders as an inert `<button>` so the markup is keyboard reachable without fabricating a destination.",
    props: [
      { name: "heading", type: "string", required: false, description: "Section heading.", defaultValue: "" },
      { name: "plans", type: "string", required: true, description: "Plans, one per line: `Name | Price | Features` (features comma-separated).", defaultValue: "Basic | $19/mo | Two visits/mo, Email support\nPremium | $49/mo | Unlimited visits, Priority support\nVIP | $99/mo | Unlimited visits, Concierge access" },
      { name: "ctaLabel", type: "string", required: false, description: "CTA label prefix (plan name is appended).", defaultValue: "Choose" },
      { name: "ctaUrl", type: "string", required: false, description: "Optional CTA destination URL.", defaultValue: "" },
    ],
  },
  {
    type: "survey",
    editorKey: "Survey",
    label: "Survey",
    description: "Single-question, single-select survey rendered as a static `<form>` with real `<input type=\"radio\">` controls. Options come from a newline-delimited textarea; duplicates and blanks are dropped and the list is capped to 20 entries.",
    usageGuidance: "Use to gather quick preference signals on landing pages. Submission URL routes through the URL safelist; omit `action` to publish a disabled form. The submitted field name is validated against a safe pattern (`[a-zA-Z][a-zA-Z0-9_]*`).",
    props: [
      { name: "question", type: "string", required: false, description: "Survey question.", defaultValue: "What matters most to you?" },
      { name: "options", type: "string", required: true, description: "Options, one per line.", defaultValue: "Quality\nPrice\nSpeed\nSupport" },
      { name: "buttonLabel", type: "string", required: false, description: "Submit button label.", defaultValue: "Submit Answer" },
      { name: "action", type: "string", required: false, description: "Form POST endpoint. Omit to render a disabled form.", defaultValue: "" },
      { name: "questionName", type: "string", required: false, description: "Submitted field name. Validated against `[a-zA-Z][a-zA-Z0-9_]*`.", defaultValue: "survey_answer" },
    ],
  },
  {
    type: "call-booking",
    editorKey: "CallBooking",
    label: "Call Booking",
    description: "Compact \"schedule a call\" card with primary CTA and optional dial-direct phone link. Phone numbers are stripped of non-dial characters before becoming a `tel:` link; a number with fewer than 5 dialable digits is rendered as plain text.",
    usageGuidance: "Use on sales / consult pages. CTAs route through the URL safelist; without a safe `buttonUrl` the card renders an inert `<button>` so the markup is keyboard reachable without fabricating a destination.",
    props: [
      { name: "heading", type: "string", required: false, description: "Card heading.", defaultValue: "Schedule a Call" },
      { name: "description", type: "string", required: false, description: "Supporting copy.", defaultValue: "" },
      { name: "duration", type: "string", required: false, description: "Display-only duration label (e.g. `15 min`).", defaultValue: "15 min" },
      { name: "buttonLabel", type: "string", required: false, description: "Primary CTA label.", defaultValue: "Pick a Time" },
      { name: "buttonUrl", type: "string", required: false, description: "Primary CTA destination URL.", defaultValue: "" },
      { name: "phone", type: "string", required: false, description: "Optional phone number (digits, +, -, spaces).", defaultValue: "" },
      { name: "phoneLabel", type: "string", required: false, description: "Label shown before the phone link.", defaultValue: "Or call directly" },
    ],
  },
];

export type ComponentType = (typeof SHARED_BLOCK_REGISTRY_DATA)[number]["type"];

export const SHARED_BLOCK_REGISTRY: readonly BlockDefinition[] = SHARED_BLOCK_REGISTRY_DATA.map(
  (entry) => ({
    ...entry,
    outputs: BLOCK_OUTPUTS_BY_TYPE[entry.type as keyof typeof BLOCK_OUTPUTS_BY_TYPE] ?? DEFAULT_WEB_BLOCK_OUTPUTS,
  }),
);

export const COMPONENT_TYPES = SHARED_BLOCK_REGISTRY_DATA.map(
  (entry) => entry.type,
) as ComponentType[];

const BLOCKS_BY_TYPE = new Map<ComponentType, BlockDefinition>(
  SHARED_BLOCK_REGISTRY.map((entry) => [entry.type, entry]),
);

export function getBlockDefinition(type: ComponentType): BlockDefinition | undefined {
  return BLOCKS_BY_TYPE.get(type);
}

export function normalizeBlockPropsForRender(type: string, props: BlockProps): BlockProps {
  const entry = BLOCKS_BY_TYPE.get(type as ComponentType);
  if (!entry?.normalizeForRender) return props;
  return entry.normalizeForRender(props);
}

export function getBlockOutputContract(type: ComponentType): BlockOutputContract | undefined {
  return BLOCKS_BY_TYPE.get(type)?.outputs;
}

export function getBlockOutputSupport(
  type: ComponentType,
  outputKey: CanonicalOutputKey,
): BlockOutputSupport | undefined {
  return BLOCKS_BY_TYPE.get(type)?.outputs.support.find((entry) => entry.outputKey === outputKey);
}

export function isBlockSupportedInOutput(
  type: ComponentType,
  outputKey: CanonicalOutputKey,
): boolean {
  return getBlockOutputSupport(type, outputKey)?.level === "full";
}

export function buildEditorComponentMap<T>(
  registry: readonly Pick<BlockDefinition, "editorKey">[],
  definitions: Record<string, T>,
): Record<string, T> {
  const components: Record<string, T> = {};

  for (const entry of registry) {
    if (!entry.editorKey) continue;
    const definition = definitions[entry.editorKey];
    if (definition) {
      components[entry.editorKey] = definition;
    }
  }

  return components;
}

export function serializeBlockCatalog(): string {
  const lines: string[] = ["## Available Components\n"];

  for (const block of SHARED_BLOCK_REGISTRY) {
    lines.push(`### ${block.label} (type: "${block.type}")`);
    lines.push(block.description);
    lines.push(`Usage: ${block.usageGuidance}`);
    lines.push(`Capabilities: ${block.outputs.capabilities.join(", ")}`);
    lines.push(
      `Outputs: ${block.outputs.support.map((entry) => `${entry.outputKey}=${entry.level}`).join(", ")}`,
    );
    lines.push("Props:");

    for (const prop of block.props) {
      const requiredLabel = prop.required ? "REQUIRED" : "optional";
      const defaultLabel = prop.defaultValue ? ` (default: ${prop.defaultValue})` : "";
      lines.push(`  - ${prop.name}: ${prop.type} [${requiredLabel}]${defaultLabel} — ${prop.description}`);
    }

    lines.push("");
  }

  return lines.join("\n");
}
